// Generated copy for the v2 landing (LandingV2 / ComposerV2 / HeaderV2 /
// StatsBand) in all supported languages. Registered lazily by
// registerLandingV2I18n.ts under the `landingV2` key.
import type { LanguageCode } from "./index";

const landingV2: Record<LanguageCode, Record<string, unknown>> = {
  en: {
    "hero": {
      "h1": "Turn any song into a music video.",
      "sub": "Bring a song. Pick a format. Direct your music video in a few clicks."
    },
    "picker": {
      "head": "Pick a format",
      "music": "Music video",
      "musicSub": "Narrative scenes",
      "singing": "Singing video",
      "singingSub": "Lip-sync performance",
      "dance": "Dance video",
      "danceSub": "Choreographed performance",
      "beta": "Beta",
      "danceBetaTip": "Full-song choreography planned section by section. The move library is still growing.",
      "lyric": "Lyric video",
      "lyricSub": "Words on screen",
      "song": "Song",
      "songSub": "Audio only",
      "mute": "Mute demo",
      "soundOn": "Play demo with sound"
    },
    "mine": {
      "projects": "My projects",
      "assets": "My assets"
    },
    "gallery": {
      "h2": "Music videos created with Solmi",
      "browse": "Browse all"
    },
    "hiw": {
      "h2": "How it works",
      "step": "Step {{n}}",
      "play": "Play the finished video",
      "b1h": "Add music",
      "b1p": "Paste a link, upload a file, or choose from your library.",
      "b1d": "Link · Upload · Library",
      "b2h": "Analyze & style",
      "b2p": "We read tempo, key and sections, then you pick a director's style for the track.",
      "b2d": "BPM · Key · Sections · Director picks",
      "b3h": "Cast & references",
      "b3p": "Character, looks and location — your photo or a performer we suggest.",
      "b3d": "Character · Looks · Location",
      "b4h": "Storyboard & render",
      "b4p": "Every shot is boarded first; regenerate anything, then render and download.",
      "b4d": "Frames · Regenerate · Timeline · Download",
      "c1": "Paste a link, upload a file, or pick one from your library — the song comes first.",
      "c2": "We read tempo, key, sections and lyrics, so every cut lands on the music.",
      "c3": "The director's picks for your track — each style steers the brief, the references and the storyboard.",
      "c4": "Character, looks and location are locked as references, so the same face appears in every shot.",
      "c5": "Every shot is boarded before a frame renders, and anything you dislike can be regenerated.",
      "c6": "The final cut renders in minutes. Preview it on the timeline, download it, or share the link."
    },
    "faq": {
      "kicker": "Quick answers",
      "h2": "Frequently asked questions.",
      "q1": "How long does it take to make a music video?",
      "a1": "The free five-second preview renders in about twenty seconds. A full-length video takes roughly three minutes once you have approved the storyboard — you are not waiting on a queue while you decide.",
      "q2": "What audio can I bring?",
      "a2": "Upload a file, choose something from your library, or paste a TikTok or YouTube link. If you do not have a track to hand, try one of the sample songs so you can see what Solmi does before committing anything.",
      "q3": "Do I need the rights to the song?",
      "a3": "For anything you publish, yes — bring music you own or are licensed to use. Videos you make with Solmi are yours: you keep the rights to what you create here.",
      "q4": "What formats and resolutions can I export?",
      "a4": "Landscape 16:9 and vertical 9:16, at up to 1080p — with captions optional on every export.",
      "q5": "Is there a free plan?",
      "a5": "Your first five-second video is free and needs no card. You will be asked to sign in before it renders, so the result is saved to your library rather than lost. Credit packs and subscriptions unlock full-length renders.",
      "q6": "Does Solmi handle lyrics and other languages?",
      "a6": "Solmi reads tempo, key, sections and lyrics, and uses them to time the cuts — so the edit lands on the song rather than beside it. Creators have made videos in English, Spanish, French, Polish, Romanian and Chinese, among others."
    },
    "app": {
      "kicker": "iOS and Android",
      "h2": "Take Solmi with you.",
      "p": "Create songs, generate music videos, manage your library, and use your Solmi credits from the iPhone, iPad and Android app.",
      "storeSmall": "Download on the",
      "storeBig": "App Store",
      "playSmall": "Get it on",
      "playBig": "Google Play",
      "qrAria": "Open Solmi app download QR link",
      "qrAlt": "QR code for the Solmi app download page",
      "scan": "Scan to download",
      "qrNote": "The QR opens a Solmi download page that routes phones to the right store."
    },
    "close": {
      "h2a": "Your track already has a world.",
      "h2b": "Let's go find it.",
      "p": "Pick a format, bring a song, and watch the first five seconds render."
    },
    "cta": {
      "music": "Make music video",
      "short": "Make video"
    },
    "dock": {
      "format": "Format",
      "notPicked": "Not picked yet",
      "pickFormat": "Pick a format",
      "song": "Song",
      "noSong": "No song yet"
    },
    "footer": {
      "gallery": "Gallery",
      "pricing": "Pricing",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Terms",
      "privacy": "Privacy"
    },
    "stats": {
      "aria": "Solmi by the numbers",
      "creators": "Creators worldwide",
      "seconds": "Seconds of video rendered",
      "countries": "Countries & regions"
    },
    "header": {
      "create": "Create",
      "musicVideo": "Music Video",
      "image": "Image",
      "video": "Video",
      "imageTools": "Image tools",
      "videoTools": "Video tools",
      "myProjects": "My Projects",
      "login": "Log in",
      "join": "Join Solmi for free",
      "joinShort": "Join free",
      "theme": "Switch theme",
      "language": "Language",
      "menu": "Menu",
      "discord": "Join our Discord",
      "rewards": "Rewards",
      "shareEarn": "Share & Earn",
      "inviteEarn": "Invite & Earn",
      "affiliate": "Affiliate Program",
      "account": "Account",
      "member": "Solmi member",
      "myProjectsMenu": "My projects"
    },
    "composer": {
      "placeholder": "Upload a song or paste a {{platform}} link",
      "describe": "Describe the song you want",
      "promptVideo": "Describe your video (optional)",
      "creating": "Creating…",
      "fetching": "Fetching audio…",
      "uploadingPct": "Uploading {{pct}}%",
      "uploadingAudio": "Uploading audio…",
      "createLyric": "Create lyric video",
      "makeSinging": "Make singing video",
      "makeDance": "Make dance video",
      "createSong": "Create song",
      "tooLarge": "Audio files must be {{mb}}MB or smaller.",
      "uploaded": "Audio uploaded",
      "uploadFailed": "Upload failed",
      "couldNotSave": "Could not save this file before sign-in. Please try again.",
      "invalidLink": "That doesn't look like a valid link.",
      "spotify": "Spotify links only provide a 30-second preview, so they're not supported. Upload the audio file or paste a YouTube link instead.",
      "extracting": "Extracting audio from {{platform}}…",
      "attachedFrom": "Attached from {{platform}}",
      "couldNotExtract": "Could not extract audio from that link.",
      "privatePaid": "Private projects are available on paid plans. Upgrade to make this project private.",
      "noAudio": "This track has no audio yet.",
      "processing": "Still processing that audio — try again in a moment.",
      "couldNotStart": "Could not start the project.",
      "untitled": "Untitled",
      "untitledProject": "Untitled project",
      "reference": "Reference",
      "attachFirst": "Select a song or paste a link to get started",
      "describeFirst": "Describe the song you want first",
      "selectSong": "Select a song",
      "images": "Images",
      "imagesOne": "1 image",
      "imagesCount": "{{count}} images",
      "imagesTitle": "Add reference images",
      "resolutionTitle": "Export resolution",
      "publicProject": "Public project",
      "privateProject": "Private project",
      "publicTitle": "Public — shown in the Solmi gallery. Click to make private.",
      "privateTitle": "Private — only you can see it. Click to make public.",
      "more": "More",
      "aspect": "Aspect ratio",
      "captions": "Captions",
      "captionsOff": "Off",
      "captionsHint": "Burn styled lyrics into the exported video",
      "captionsOn": "Captions on ({{label}}) — click to change the style",
      "removeCaptions": "Remove captions",
      "uploadSong": "Upload a song",
      "fileTypes": "MP3 · WAV · M4A · MP4 · up to {{mb}}MB",
      "samples": "Sample songs",
      "mySongs": "My songs",
      "signInSongs": "Sign in to use your Solmi songs.",
      "loading": "Loading…",
      "noSongs": "No songs yet.",
      "playPreview": "Play preview",
      "pausePreview": "Pause preview",
      "removeSong": "Remove song",
      "upgrade1080": "720p and 1080p exports are available on paid plans. Upgrade to unlock them."
    }
  },
  es: {
    "hero": {
      "h1": "Convierte cualquier canción en un videoclip.",
      "sub": "Trae una canción. Elige un formato. Dirige tu videoclip en unos pocos clics."
    },
    "picker": {
      "head": "Elige un formato",
      "music": "Videoclip",
      "musicSub": "Escenas narrativas",
      "singing": "Video cantado",
      "singingSub": "Actuación con sincronía labial",
      "lyric": "Video con letra",
      "lyricSub": "Letra en pantalla",
      "song": "Canción",
      "songSub": "Solo audio",
      "mute": "Silenciar demo",
      "soundOn": "Reproducir demo con sonido"
    },
    "mine": {
      "projects": "Mis proyectos",
      "assets": "Mis archivos"
    },
    "gallery": {
      "h2": "Videoclips creados con Solmi",
      "browse": "Ver todos"
    },
    "hiw": {
      "h2": "Cómo funciona",
      "step": "Paso {{n}}",
      "play": "Reproducir el video final",
      "b1h": "Añade música",
      "b1p": "Pega un enlace, sube un archivo o elige de tu biblioteca.",
      "b1d": "Enlace · Subida · Biblioteca",
      "b2h": "Análisis y estilo",
      "b2p": "Leemos tempo, tonalidad y secciones; luego eliges un estilo de director para el tema.",
      "b2d": "BPM · Tonalidad · Secciones · Estilos",
      "b3h": "Reparto y referencias",
      "b3p": "Personaje, look y localización: tu foto o un intérprete que te sugerimos.",
      "b3d": "Personaje · Look · Localización",
      "b4h": "Storyboard y render",
      "b4p": "Cada plano se dibuja primero; regenera lo que quieras, luego renderiza y descarga.",
      "b4d": "Planos · Regenerar · Línea de tiempo · Descargar",
      "c1": "Pega un enlace, sube un archivo o elige uno de tu biblioteca: la canción va primero.",
      "c2": "Leemos tempo, tonalidad, secciones y letra para que cada corte caiga sobre la música.",
      "c3": "Las propuestas del director para tu tema: cada estilo guía el brief, las referencias y el storyboard.",
      "c4": "Personaje, look y localización quedan fijados como referencias, así el mismo rostro aparece en cada plano.",
      "c5": "Cada plano se dibuja antes de renderizar un solo fotograma, y puedes regenerar lo que no te guste.",
      "c6": "El montaje final se renderiza en minutos. Míralo en la línea de tiempo, descárgalo o comparte el enlace."
    },
    "faq": {
      "kicker": "Respuestas rápidas",
      "h2": "Preguntas frecuentes.",
      "q1": "¿Cuánto tarda en hacerse un videoclip?",
      "a1": "La vista previa gratuita de cinco segundos se renderiza en unos veinte segundos. Un video completo tarda unos tres minutos una vez que apruebas el storyboard; no esperas en cola mientras decides.",
      "q2": "¿Qué audio puedo usar?",
      "a2": "Sube un archivo, elige algo de tu biblioteca o pega un enlace de TikTok o YouTube. Si no tienes un tema a mano, prueba una de las canciones de muestra para ver qué hace Solmi antes de comprometerte.",
      "q3": "¿Necesito los derechos de la canción?",
      "a3": "Para todo lo que publiques, sí: usa música tuya o con licencia. Los videos que hagas con Solmi son tuyos: conservas los derechos de lo que creas aquí.",
      "q4": "¿Qué formatos y resoluciones puedo exportar?",
      "a4": "Horizontal 16:9 y vertical 9:16, hasta 1080p, con subtítulos opcionales en cada exportación.",
      "q5": "¿Hay un plan gratuito?",
      "a5": "Tu primer video de cinco segundos es gratis y no requiere tarjeta. Te pediremos iniciar sesión antes de renderizar para guardar el resultado en tu biblioteca. Los packs de créditos y las suscripciones desbloquean renders completos.",
      "q6": "¿Solmi entiende letras y otros idiomas?",
      "a6": "Solmi lee tempo, tonalidad, secciones y letra, y los usa para sincronizar los cortes, así el montaje va con la canción. Los creadores han hecho videos en inglés, español, francés, polaco, rumano y chino, entre otros."
    },
    "app": {
      "kicker": "iOS y Android",
      "h2": "Lleva Solmi contigo.",
      "p": "Crea canciones, genera videoclips, gestiona tu biblioteca y usa tus créditos de Solmi desde la app para iPhone, iPad y Android.",
      "storeSmall": "Descargar en el",
      "storeBig": "App Store",
      "playSmall": "Disponible en",
      "playBig": "Google Play",
      "qrAria": "Abrir el enlace QR de descarga de la app Solmi",
      "qrAlt": "Código QR de la página de descarga de la app Solmi",
      "scan": "Escanea para descargar",
      "qrNote": "El QR abre una página de descarga de Solmi que lleva a cada teléfono a su tienda."
    },
    "close": {
      "h2a": "Tu canción ya tiene un mundo.",
      "h2b": "Vamos a encontrarlo.",
      "p": "Elige un formato, trae una canción y mira renderizarse los primeros cinco segundos."
    },
    "cta": {
      "music": "Crear videoclip",
      "short": "Crear video"
    },
    "dock": {
      "format": "Formato",
      "notPicked": "Sin elegir",
      "pickFormat": "Elige un formato",
      "song": "Canción",
      "noSong": "Sin canción"
    },
    "footer": {
      "gallery": "Galería",
      "pricing": "Precios",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Términos",
      "privacy": "Privacidad"
    },
    "stats": {
      "aria": "Solmi en cifras",
      "creators": "Creadores en el mundo",
      "seconds": "Segundos de video renderizados",
      "countries": "Países y regiones"
    },
    "header": {
      "create": "Crear",
      "myProjects": "Mis proyectos",
      "login": "Iniciar sesión",
      "join": "Únete a Solmi gratis",
      "joinShort": "Únete gratis",
      "theme": "Cambiar tema",
      "language": "Idioma",
      "menu": "Menú",
      "discord": "Únete a nuestro Discord",
      "rewards": "Recompensas",
      "shareEarn": "Comparte y gana",
      "inviteEarn": "Invita y gana",
      "affiliate": "Programa de afiliados",
      "account": "Cuenta",
      "member": "Miembro de Solmi",
      "myProjectsMenu": "Mis proyectos"
    },
    "composer": {
      "placeholder": "Sube una canción o pega un enlace de {{platform}}",
      "describe": "Describe la canción que quieres",
      "promptVideo": "Describe tu videoclip (opcional)",
      "creating": "Creando…",
      "fetching": "Obteniendo audio…",
      "uploadingPct": "Subiendo {{pct}}%",
      "uploadingAudio": "Subiendo audio…",
      "createLyric": "Crear video con letra",
      "makeSinging": "Crear video cantado",
      "createSong": "Crear canción",
      "tooLarge": "Los archivos de audio deben pesar {{mb}}MB o menos.",
      "uploaded": "Audio subido",
      "uploadFailed": "Error al subir",
      "couldNotSave": "No se pudo guardar el archivo antes de iniciar sesión. Inténtalo de nuevo.",
      "invalidLink": "Eso no parece un enlace válido.",
      "spotify": "Los enlaces de Spotify solo ofrecen una vista previa de 30 segundos, así que no son compatibles. Sube el archivo de audio o pega un enlace de YouTube.",
      "extracting": "Extrayendo audio de {{platform}}…",
      "attachedFrom": "Adjuntado desde {{platform}}",
      "couldNotExtract": "No se pudo extraer el audio de ese enlace.",
      "privatePaid": "Los proyectos privados están disponibles en los planes de pago. Mejora tu plan para hacerlo privado.",
      "noAudio": "Esta pista aún no tiene audio.",
      "processing": "Todavía procesando ese audio; inténtalo en un momento.",
      "couldNotStart": "No se pudo iniciar el proyecto.",
      "untitled": "Sin título",
      "untitledProject": "Proyecto sin título",
      "reference": "Referencia",
      "attachFirst": "Elige una canción o pega un enlace para empezar",
      "describeFirst": "Primero describe la canción que quieres",
      "selectSong": "Elegir canción",
      "images": "Imágenes",
      "imagesOne": "1 imagen",
      "imagesCount": "{{count}} imágenes",
      "imagesTitle": "Añadir imágenes de referencia",
      "resolutionTitle": "Resolución de exportación",
      "publicProject": "Proyecto público",
      "privateProject": "Proyecto privado",
      "publicTitle": "Público: se muestra en la galería de Solmi. Clic para hacerlo privado.",
      "privateTitle": "Privado: solo tú puedes verlo. Clic para hacerlo público.",
      "more": "Más",
      "aspect": "Relación de aspecto",
      "captions": "Subtítulos",
      "captionsOff": "Desactivados",
      "captionsHint": "Incrusta la letra con estilo en el video exportado",
      "captionsOn": "Subtítulos activados ({{label}}): clic para cambiar el estilo",
      "removeCaptions": "Quitar subtítulos",
      "uploadSong": "Subir una canción",
      "fileTypes": "MP3 · WAV · M4A · MP4 · hasta {{mb}}MB",
      "samples": "Canciones de muestra",
      "mySongs": "Mis canciones",
      "signInSongs": "Inicia sesión para usar tus canciones de Solmi.",
      "loading": "Cargando…",
      "noSongs": "Aún no hay canciones.",
      "playPreview": "Reproducir vista previa",
      "pausePreview": "Pausar vista previa",
      "removeSong": "Quitar canción",
      "upgrade1080": "Las exportaciones en 720p y 1080p están disponibles en los planes de pago. Mejora tu plan para desbloquearlas."
    }
  },
  ja: {
    "hero": {
      "h1": "どんな曲もミュージックビデオに。",
      "sub": "曲を用意して、フォーマットを選び、数クリックでミュージックビデオを演出。"
    },
    "picker": {
      "head": "フォーマットを選ぶ",
      "music": "ミュージックビデオ",
      "musicSub": "物語のシーン",
      "singing": "歌唱ビデオ",
      "singingSub": "リップシンクの演奏",
      "lyric": "リリックビデオ",
      "lyricSub": "画面に歌詞",
      "song": "楽曲",
      "songSub": "音声のみ",
      "mute": "デモをミュート",
      "soundOn": "デモを音付きで再生"
    },
    "mine": {
      "projects": "マイプロジェクト",
      "assets": "マイファイル"
    },
    "gallery": {
      "h2": "Solmiで作られたミュージックビデオ",
      "browse": "すべて見る"
    },
    "hiw": {
      "h2": "使い方",
      "step": "ステップ {{n}}",
      "play": "完成した動画を再生",
      "b1h": "音楽を追加",
      "b1p": "リンクを貼る、ファイルをアップロード、またはライブラリから選択。",
      "b1d": "リンク · アップロード · ライブラリ",
      "b2h": "解析とスタイル",
      "b2p": "テンポ・キー・構成を読み取り、曲に合うディレクタースタイルを選びます。",
      "b2d": "BPM · キー · 構成 · スタイル",
      "b3h": "キャストとリファレンス",
      "b3p": "キャラクター・ルック・ロケーション。あなたの写真か、提案するパフォーマーで。",
      "b3d": "キャラクター · ルック · ロケーション",
      "b4h": "絵コンテとレンダリング",
      "b4p": "すべてのショットを先に絵コンテ化。気になる所は再生成して、レンダリングとダウンロード。",
      "b4d": "フレーム · 再生成 · タイムライン · ダウンロード",
      "c1": "リンクを貼る、ファイルをアップロード、ライブラリから選ぶ。まずは曲から。",
      "c2": "テンポ・キー・構成・歌詞を読み取り、すべてのカットを音楽に合わせます。",
      "c3": "あなたの曲に対するディレクターの提案。各スタイルがブリーフ、リファレンス、絵コンテを方向づけます。",
      "c4": "キャラクター・ルック・ロケーションをリファレンスとして固定し、すべてのショットで同じ顔が登場します。",
      "c5": "1フレームもレンダリングする前にすべてのショットを絵コンテ化。気に入らない部分は再生成できます。",
      "c6": "最終カットは数分でレンダリング。タイムラインで確認し、ダウンロードやリンク共有ができます。"
    },
    "faq": {
      "kicker": "簡単な回答",
      "h2": "よくある質問",
      "q1": "ミュージックビデオの制作にはどれくらいかかりますか？",
      "a1": "無料の5秒プレビューは約20秒でレンダリングされます。絵コンテを承認した後、フルサイズの動画は約3分。判断している間に順番待ちをする必要はありません。",
      "q2": "どんな音源が使えますか？",
      "a2": "ファイルのアップロード、ライブラリからの選択、TikTokやYouTubeのリンク貼り付けに対応。手元に曲がなければ、サンプル曲でSolmiの動きを試せます。",
      "q3": "曲の権利は必要ですか？",
      "a3": "公開するものについては必要です。自分が権利を持つ、またはライセンスを受けた音楽をお使いください。Solmiで作った動画はあなたのもの。ここで作ったものの権利はあなたに帰属します。",
      "q4": "書き出せるフォーマットと解像度は？",
      "a4": "横16:9と縦9:16、最大1080p。書き出しごとに字幕も任意で追加できます。",
      "q5": "無料プランはありますか？",
      "a5": "最初の5秒動画は無料で、カード登録も不要です。レンダリング前にログインをお願いするので、結果はライブラリに保存されます。クレジットパックとサブスクリプションでフルレングスのレンダリングが解放されます。",
      "q6": "歌詞や他の言語にも対応していますか？",
      "a6": "Solmiはテンポ・キー・構成・歌詞を読み取ってカットのタイミングを決めるので、編集が曲にぴたりと合います。英語、スペイン語、フランス語、ポーランド語、ルーマニア語、中国語などの動画が作られています。"
    },
    "app": {
      "kicker": "iOSとAndroid",
      "h2": "Solmiをいつでも手元に。",
      "p": "iPhone・iPad・Androidアプリで、曲の作成、ミュージックビデオの生成、ライブラリ管理、Solmiクレジットの利用ができます。",
      "storeSmall": "ダウンロードは",
      "storeBig": "App Store",
      "playSmall": "入手は",
      "playBig": "Google Play",
      "qrAria": "SolmiアプリのダウンロードQRリンクを開く",
      "qrAlt": "SolmiアプリダウンロードページのQRコード",
      "scan": "スキャンしてダウンロード",
      "qrNote": "QRを読み取ると、端末に合ったストアへ案内するSolmiのダウンロードページが開きます。"
    },
    "close": {
      "h2a": "あなたの曲には、もう世界がある。",
      "h2b": "一緒に見つけに行こう。",
      "p": "フォーマットを選び、曲を用意して、最初の5秒がレンダリングされるのを見てください。"
    },
    "cta": {
      "music": "ミュージックビデオを作る",
      "short": "動画を作る"
    },
    "dock": {
      "format": "フォーマット",
      "notPicked": "未選択",
      "pickFormat": "フォーマットを選ぶ",
      "song": "楽曲",
      "noSong": "曲が未設定"
    },
    "footer": {
      "gallery": "ギャラリー",
      "pricing": "料金",
      "blog": "ブログ",
      "discord": "Discord",
      "terms": "利用規約",
      "privacy": "プライバシー"
    },
    "stats": {
      "aria": "数字で見るSolmi",
      "creators": "世界のクリエイター",
      "seconds": "レンダリングした動画の秒数",
      "countries": "国と地域"
    },
    "header": {
      "create": "作成",
      "myProjects": "マイプロジェクト",
      "login": "ログイン",
      "join": "無料でSolmiに参加",
      "joinShort": "無料で参加",
      "theme": "テーマを切り替え",
      "language": "言語",
      "menu": "メニュー",
      "discord": "Discordに参加",
      "rewards": "リワード",
      "shareEarn": "シェアして獲得",
      "inviteEarn": "招待して獲得",
      "affiliate": "アフィリエイトプログラム",
      "account": "アカウント",
      "member": "Solmiメンバー",
      "myProjectsMenu": "マイプロジェクト"
    },
    "composer": {
      "placeholder": "曲をアップロードするか{{platform}}のリンクを貼り付け",
      "describe": "作りたい曲を説明してください",
      "promptVideo": "ビデオの内容を入力（任意）",
      "creating": "作成中…",
      "fetching": "音声を取得中…",
      "uploadingPct": "アップロード中 {{pct}}%",
      "uploadingAudio": "音声をアップロード中…",
      "createLyric": "リリックビデオを作る",
      "makeSinging": "歌唱ビデオを作る",
      "createSong": "曲を作る",
      "tooLarge": "音声ファイルは{{mb}}MB以下にしてください。",
      "uploaded": "音声をアップロードしました",
      "uploadFailed": "アップロードに失敗しました",
      "couldNotSave": "ログイン前にファイルを保存できませんでした。もう一度お試しください。",
      "invalidLink": "有効なリンクではないようです。",
      "spotify": "Spotifyのリンクは30秒のプレビューしか提供しないため対応していません。音声ファイルをアップロードするか、YouTubeのリンクを貼ってください。",
      "extracting": "{{platform}}から音声を抽出中…",
      "attachedFrom": "{{platform}}から追加しました",
      "couldNotExtract": "そのリンクから音声を抽出できませんでした。",
      "privatePaid": "非公開プロジェクトは有料プランで利用できます。アップグレードして非公開にしてください。",
      "noAudio": "このトラックにはまだ音声がありません。",
      "processing": "音声を処理中です。少し待ってからお試しください。",
      "couldNotStart": "プロジェクトを開始できませんでした。",
      "untitled": "無題",
      "untitledProject": "無題のプロジェクト",
      "reference": "リファレンス",
      "attachFirst": "曲を選ぶかリンクを貼って始めましょう",
      "describeFirst": "まず作りたい曲を説明してください",
      "selectSong": "曲を選ぶ",
      "images": "画像",
      "imagesOne": "画像1枚",
      "imagesCount": "画像{{count}}枚",
      "imagesTitle": "リファレンス画像を追加",
      "resolutionTitle": "書き出し解像度",
      "publicProject": "公開プロジェクト",
      "privateProject": "非公開プロジェクト",
      "publicTitle": "公開 — Solmiギャラリーに表示されます。クリックで非公開に。",
      "privateTitle": "非公開 — あなただけが見られます。クリックで公開に。",
      "more": "その他",
      "aspect": "アスペクト比",
      "captions": "字幕",
      "captionsOff": "オフ",
      "captionsHint": "スタイル付き歌詞を書き出し動画に焼き込む",
      "captionsOn": "字幕オン（{{label}}）— クリックでスタイル変更",
      "removeCaptions": "字幕を削除",
      "uploadSong": "曲をアップロード",
      "fileTypes": "MP3 · WAV · M4A · MP4 · 最大{{mb}}MB",
      "samples": "サンプル曲",
      "mySongs": "マイソング",
      "signInSongs": "ログインするとSolmiの曲を使えます。",
      "loading": "読み込み中…",
      "noSongs": "まだ曲がありません。",
      "playPreview": "プレビューを再生",
      "pausePreview": "プレビューを一時停止",
      "removeSong": "曲を外す",
      "upgrade1080": "720pと1080pの書き出しは有料プランで利用できます。アップグレードして解放してください。"
    }
  },
  pt: {
    "hero": {
      "h1": "Transforme qualquer música em um videoclipe.",
      "sub": "Traga uma música. Escolha um formato. Dirija seu videoclipe em poucos cliques."
    },
    "picker": {
      "head": "Escolha um formato",
      "music": "Videoclipe",
      "musicSub": "Cenas narrativas",
      "singing": "Vídeo cantado",
      "singingSub": "Performance com lip-sync",
      "lyric": "Vídeo com letra",
      "lyricSub": "Letra na tela",
      "song": "Música",
      "songSub": "Só áudio",
      "mute": "Silenciar demo",
      "soundOn": "Tocar demo com som"
    },
    "mine": {
      "projects": "Meus projetos",
      "assets": "Meus arquivos"
    },
    "gallery": {
      "h2": "Videoclipes criados com a Solmi",
      "browse": "Ver todos"
    },
    "hiw": {
      "h2": "Como funciona",
      "step": "Passo {{n}}",
      "play": "Assistir ao vídeo final",
      "b1h": "Adicione a música",
      "b1p": "Cole um link, envie um arquivo ou escolha da sua biblioteca.",
      "b1d": "Link · Upload · Biblioteca",
      "b2h": "Análise e estilo",
      "b2p": "Lemos andamento, tom e seções; depois você escolhe um estilo de direção para a faixa.",
      "b2d": "BPM · Tom · Seções · Estilos",
      "b3h": "Elenco e referências",
      "b3p": "Personagem, visual e locação — sua foto ou um intérprete que sugerimos.",
      "b3d": "Personagem · Visual · Locação",
      "b4h": "Storyboard e render",
      "b4p": "Cada plano é desenhado antes; regenere o que quiser, depois renderize e baixe.",
      "b4d": "Planos · Regenerar · Timeline · Download",
      "c1": "Cole um link, envie um arquivo ou escolha da sua biblioteca — a música vem primeiro.",
      "c2": "Lemos andamento, tom, seções e letra para que cada corte caia na música.",
      "c3": "As escolhas do diretor para a sua faixa — cada estilo guia o briefing, as referências e o storyboard.",
      "c4": "Personagem, visual e locação ficam fixados como referências, então o mesmo rosto aparece em todos os planos.",
      "c5": "Cada plano é desenhado antes de renderizar um único quadro, e tudo que você não gostar pode ser regenerado.",
      "c6": "O corte final renderiza em minutos. Veja na timeline, baixe ou compartilhe o link."
    },
    "faq": {
      "kicker": "Respostas rápidas",
      "h2": "Perguntas frequentes.",
      "q1": "Quanto tempo leva para fazer um videoclipe?",
      "a1": "A prévia gratuita de cinco segundos renderiza em cerca de vinte segundos. Um vídeo completo leva uns três minutos depois que você aprova o storyboard — sem esperar em fila enquanto decide.",
      "q2": "Que áudio posso usar?",
      "a2": "Envie um arquivo, escolha algo da sua biblioteca ou cole um link do TikTok ou do YouTube. Se não tiver uma faixa à mão, experimente uma das músicas de exemplo para ver o que a Solmi faz antes de se comprometer.",
      "q3": "Preciso ter os direitos da música?",
      "a3": "Para tudo que você publicar, sim — use música sua ou licenciada. Os vídeos feitos com a Solmi são seus: você mantém os direitos do que cria aqui.",
      "q4": "Quais formatos e resoluções posso exportar?",
      "a4": "Horizontal 16:9 e vertical 9:16, até 1080p — com legendas opcionais em cada exportação.",
      "q5": "Existe plano gratuito?",
      "a5": "Seu primeiro vídeo de cinco segundos é grátis e não pede cartão. Pediremos login antes de renderizar, para salvar o resultado na sua biblioteca. Pacotes de créditos e assinaturas liberam renders completos.",
      "q6": "A Solmi entende letras e outros idiomas?",
      "a6": "A Solmi lê andamento, tom, seções e letra e usa isso para sincronizar os cortes — a edição acompanha a música. Criadores já fizeram vídeos em inglês, espanhol, francês, polonês, romeno e chinês, entre outros."
    },
    "app": {
      "kicker": "iOS e Android",
      "h2": "Leve a Solmi com você.",
      "p": "Crie músicas, gere videoclipes, gerencie sua biblioteca e use seus créditos Solmi no app para iPhone, iPad e Android.",
      "storeSmall": "Baixar na",
      "storeBig": "App Store",
      "playSmall": "Disponível no",
      "playBig": "Google Play",
      "qrAria": "Abrir o link QR de download do app Solmi",
      "qrAlt": "QR code da página de download do app Solmi",
      "scan": "Escaneie para baixar",
      "qrNote": "O QR abre uma página de download da Solmi que leva cada celular à loja certa."
    },
    "close": {
      "h2a": "Sua faixa já tem um mundo.",
      "h2b": "Vamos encontrá-lo.",
      "p": "Escolha um formato, traga uma música e veja os primeiros cinco segundos renderizarem."
    },
    "cta": {
      "music": "Criar videoclipe",
      "short": "Criar vídeo"
    },
    "dock": {
      "format": "Formato",
      "notPicked": "Ainda não escolhido",
      "pickFormat": "Escolha um formato",
      "song": "Música",
      "noSong": "Sem música ainda"
    },
    "footer": {
      "gallery": "Galeria",
      "pricing": "Preços",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Termos",
      "privacy": "Privacidade"
    },
    "stats": {
      "aria": "Solmi em números",
      "creators": "Criadores no mundo",
      "seconds": "Segundos de vídeo renderizados",
      "countries": "Países e regiões"
    },
    "header": {
      "create": "Criar",
      "myProjects": "Meus projetos",
      "login": "Entrar",
      "join": "Entre na Solmi grátis",
      "joinShort": "Entrar grátis",
      "theme": "Mudar tema",
      "language": "Idioma",
      "menu": "Menu",
      "discord": "Entre no nosso Discord",
      "rewards": "Recompensas",
      "shareEarn": "Compartilhe e ganhe",
      "inviteEarn": "Convide e ganhe",
      "affiliate": "Programa de afiliados",
      "account": "Conta",
      "member": "Membro Solmi",
      "myProjectsMenu": "Meus projetos"
    },
    "composer": {
      "placeholder": "Envie uma música ou cole um link do {{platform}}",
      "describe": "Descreva a música que você quer",
      "promptVideo": "Descreva seu vídeo (opcional)",
      "creating": "Criando…",
      "fetching": "Buscando áudio…",
      "uploadingPct": "Enviando {{pct}}%",
      "uploadingAudio": "Enviando áudio…",
      "createLyric": "Criar vídeo com letra",
      "makeSinging": "Criar vídeo cantado",
      "createSong": "Criar música",
      "tooLarge": "Arquivos de áudio devem ter no máximo {{mb}}MB.",
      "uploaded": "Áudio enviado",
      "uploadFailed": "Falha no envio",
      "couldNotSave": "Não foi possível salvar o arquivo antes do login. Tente de novo.",
      "invalidLink": "Isso não parece um link válido.",
      "spotify": "Links do Spotify só oferecem uma prévia de 30 segundos e não são suportados. Envie o arquivo de áudio ou cole um link do YouTube.",
      "extracting": "Extraindo áudio do {{platform}}…",
      "attachedFrom": "Anexado do {{platform}}",
      "couldNotExtract": "Não foi possível extrair o áudio desse link.",
      "privatePaid": "Projetos privados estão disponíveis nos planos pagos. Faça upgrade para deixar este projeto privado.",
      "noAudio": "Esta faixa ainda não tem áudio.",
      "processing": "Ainda processando esse áudio — tente de novo em instantes.",
      "couldNotStart": "Não foi possível iniciar o projeto.",
      "untitled": "Sem título",
      "untitledProject": "Projeto sem título",
      "reference": "Referência",
      "attachFirst": "Escolha uma música ou cole um link para começar",
      "describeFirst": "Descreva primeiro a música que você quer",
      "selectSong": "Escolher música",
      "images": "Imagens",
      "imagesOne": "1 imagem",
      "imagesCount": "{{count}} imagens",
      "imagesTitle": "Adicionar imagens de referência",
      "resolutionTitle": "Resolução de exportação",
      "publicProject": "Projeto público",
      "privateProject": "Projeto privado",
      "publicTitle": "Público — aparece na galeria da Solmi. Clique para tornar privado.",
      "privateTitle": "Privado — só você vê. Clique para tornar público.",
      "more": "Mais",
      "aspect": "Proporção",
      "captions": "Legendas",
      "captionsOff": "Desativadas",
      "captionsHint": "Gravar a letra estilizada no vídeo exportado",
      "captionsOn": "Legendas ativadas ({{label}}) — clique para mudar o estilo",
      "removeCaptions": "Remover legendas",
      "uploadSong": "Enviar uma música",
      "fileTypes": "MP3 · WAV · M4A · MP4 · até {{mb}}MB",
      "samples": "Músicas de exemplo",
      "mySongs": "Minhas músicas",
      "signInSongs": "Entre para usar suas músicas da Solmi.",
      "loading": "Carregando…",
      "noSongs": "Nenhuma música ainda.",
      "playPreview": "Tocar prévia",
      "pausePreview": "Pausar prévia",
      "removeSong": "Remover música",
      "upgrade1080": "Exportações em 720p e 1080p estão disponíveis nos planos pagos. Faça upgrade para liberá-las."
    }
  },
  fr: {
    "hero": {
      "h1": "Transformez n'importe quelle chanson en clip.",
      "sub": "Apportez une chanson. Choisissez un format. Réalisez votre clip en quelques clics."
    },
    "picker": {
      "head": "Choisir un format",
      "music": "Clip musical",
      "musicSub": "Scènes narratives",
      "singing": "Vidéo chantée",
      "singingSub": "Performance en lip-sync",
      "lyric": "Vidéo lyrics",
      "lyricSub": "Paroles à l'écran",
      "song": "Chanson",
      "songSub": "Audio seul",
      "mute": "Couper le son de la démo",
      "soundOn": "Lire la démo avec le son"
    },
    "mine": {
      "projects": "Mes projets",
      "assets": "Mes fichiers"
    },
    "gallery": {
      "h2": "Clips créés avec Solmi",
      "browse": "Tout voir"
    },
    "hiw": {
      "h2": "Comment ça marche",
      "step": "Étape {{n}}",
      "play": "Lire la vidéo finale",
      "b1h": "Ajoutez la musique",
      "b1p": "Collez un lien, importez un fichier ou choisissez dans votre bibliothèque.",
      "b1d": "Lien · Import · Bibliothèque",
      "b2h": "Analyse et style",
      "b2p": "Nous lisons le tempo, la tonalité et les sections, puis vous choisissez un style de réalisateur.",
      "b2d": "BPM · Tonalité · Sections · Styles",
      "b3h": "Casting et références",
      "b3p": "Personnage, look et lieu : votre photo ou un interprète que nous proposons.",
      "b3d": "Personnage · Look · Lieu",
      "b4h": "Storyboard et rendu",
      "b4p": "Chaque plan est d'abord storyboardé ; régénérez ce que vous voulez, puis lancez le rendu et téléchargez.",
      "b4d": "Plans · Régénérer · Timeline · Télécharger",
      "c1": "Collez un lien, importez un fichier ou choisissez dans votre bibliothèque : la chanson d'abord.",
      "c2": "Nous lisons le tempo, la tonalité, les sections et les paroles pour que chaque coupe tombe sur la musique.",
      "c3": "Les propositions du réalisateur pour votre titre : chaque style oriente le brief, les références et le storyboard.",
      "c4": "Personnage, look et lieu sont verrouillés comme références : le même visage apparaît dans chaque plan.",
      "c5": "Chaque plan est storyboardé avant le rendu d'une seule image, et tout ce qui vous déplaît peut être régénéré.",
      "c6": "Le montage final est rendu en quelques minutes. Regardez-le sur la timeline, téléchargez-le ou partagez le lien."
    },
    "faq": {
      "kicker": "Réponses rapides",
      "h2": "Questions fréquentes.",
      "q1": "Combien de temps faut-il pour créer un clip ?",
      "a1": "L'aperçu gratuit de cinq secondes est rendu en une vingtaine de secondes. Une vidéo complète prend environ trois minutes une fois le storyboard approuvé ; vous n'attendez pas dans une file pendant que vous décidez.",
      "q2": "Quel audio puis-je utiliser ?",
      "a2": "Importez un fichier, choisissez dans votre bibliothèque ou collez un lien TikTok ou YouTube. Sans titre sous la main, essayez une chanson d'exemple pour voir ce que fait Solmi avant de vous engager.",
      "q3": "Dois-je détenir les droits de la chanson ?",
      "a3": "Pour tout ce que vous publiez, oui : utilisez une musique qui vous appartient ou sous licence. Les vidéos créées avec Solmi sont à vous : vous conservez les droits sur ce que vous créez ici.",
      "q4": "Quels formats et résolutions puis-je exporter ?",
      "a4": "Paysage 16:9 et vertical 9:16, jusqu'en 1080p, avec sous-titres en option à chaque export.",
      "q5": "Y a-t-il une offre gratuite ?",
      "a5": "Votre première vidéo de cinq secondes est gratuite et sans carte. Vous devrez vous connecter avant le rendu pour que le résultat soit enregistré dans votre bibliothèque. Les packs de crédits et les abonnements débloquent les rendus complets.",
      "q6": "Solmi gère-t-il les paroles et d'autres langues ?",
      "a6": "Solmi lit le tempo, la tonalité, les sections et les paroles pour caler les coupes : le montage suit la chanson. Des créateurs ont réalisé des vidéos en anglais, espagnol, français, polonais, roumain et chinois, entre autres."
    },
    "app": {
      "kicker": "iOS et Android",
      "h2": "Emportez Solmi partout.",
      "p": "Créez des chansons, générez des clips, gérez votre bibliothèque et utilisez vos crédits Solmi depuis l'app iPhone, iPad et Android.",
      "storeSmall": "Télécharger dans l'",
      "storeBig": "App Store",
      "playSmall": "Disponible sur",
      "playBig": "Google Play",
      "qrAria": "Ouvrir le lien QR de téléchargement de l'app Solmi",
      "qrAlt": "QR code de la page de téléchargement de l'app Solmi",
      "scan": "Scannez pour télécharger",
      "qrNote": "Le QR ouvre une page de téléchargement Solmi qui redirige vers la bonne boutique."
    },
    "close": {
      "h2a": "Votre titre a déjà un monde.",
      "h2b": "Allons le trouver.",
      "p": "Choisissez un format, apportez une chanson et regardez les cinq premières secondes se rendre."
    },
    "cta": {
      "music": "Créer un clip",
      "short": "Créer la vidéo"
    },
    "dock": {
      "format": "Format",
      "notPicked": "Pas encore choisi",
      "pickFormat": "Choisir un format",
      "song": "Chanson",
      "noSong": "Pas encore de chanson"
    },
    "footer": {
      "gallery": "Galerie",
      "pricing": "Tarifs",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Conditions",
      "privacy": "Confidentialité"
    },
    "stats": {
      "aria": "Solmi en chiffres",
      "creators": "Créateurs dans le monde",
      "seconds": "Secondes de vidéo rendues",
      "countries": "Pays et régions"
    },
    "header": {
      "create": "Créer",
      "myProjects": "Mes projets",
      "login": "Connexion",
      "join": "Rejoindre Solmi gratuitement",
      "joinShort": "Rejoindre",
      "theme": "Changer de thème",
      "language": "Langue",
      "menu": "Menu",
      "discord": "Rejoindre notre Discord",
      "rewards": "Récompenses",
      "shareEarn": "Partagez et gagnez",
      "inviteEarn": "Invitez et gagnez",
      "affiliate": "Programme d'affiliation",
      "account": "Compte",
      "member": "Membre Solmi",
      "myProjectsMenu": "Mes projets"
    },
    "composer": {
      "placeholder": "Importez une chanson ou collez un lien {{platform}}",
      "describe": "Décrivez la chanson que vous voulez",
      "promptVideo": "Décrivez votre clip (facultatif)",
      "creating": "Création…",
      "fetching": "Récupération de l'audio…",
      "uploadingPct": "Import {{pct}} %",
      "uploadingAudio": "Import de l'audio…",
      "createLyric": "Créer une vidéo lyrics",
      "makeSinging": "Créer une vidéo chantée",
      "createSong": "Créer une chanson",
      "tooLarge": "Les fichiers audio doivent faire {{mb}} Mo au maximum.",
      "uploaded": "Audio importé",
      "uploadFailed": "Échec de l'import",
      "couldNotSave": "Impossible d'enregistrer ce fichier avant la connexion. Réessayez.",
      "invalidLink": "Ce lien ne semble pas valide.",
      "spotify": "Les liens Spotify ne fournissent qu'un aperçu de 30 secondes et ne sont pas pris en charge. Importez le fichier audio ou collez un lien YouTube.",
      "extracting": "Extraction de l'audio depuis {{platform}}…",
      "attachedFrom": "Ajouté depuis {{platform}}",
      "couldNotExtract": "Impossible d'extraire l'audio de ce lien.",
      "privatePaid": "Les projets privés sont réservés aux offres payantes. Passez à une offre supérieure pour rendre ce projet privé.",
      "noAudio": "Ce titre n'a pas encore d'audio.",
      "processing": "Audio encore en traitement, réessayez dans un instant.",
      "couldNotStart": "Impossible de démarrer le projet.",
      "untitled": "Sans titre",
      "untitledProject": "Projet sans titre",
      "reference": "Référence",
      "attachFirst": "Choisissez une chanson ou collez un lien pour commencer",
      "describeFirst": "Décrivez d'abord la chanson que vous voulez",
      "selectSong": "Choisir une chanson",
      "images": "Images",
      "imagesOne": "1 image",
      "imagesCount": "{{count}} images",
      "imagesTitle": "Ajouter des images de référence",
      "resolutionTitle": "Résolution d'export",
      "publicProject": "Projet public",
      "privateProject": "Projet privé",
      "publicTitle": "Public : visible dans la galerie Solmi. Cliquez pour le rendre privé.",
      "privateTitle": "Privé : vous seul pouvez le voir. Cliquez pour le rendre public.",
      "more": "Plus",
      "aspect": "Format d'image",
      "captions": "Sous-titres",
      "captionsOff": "Désactivés",
      "captionsHint": "Incruster les paroles stylisées dans la vidéo exportée",
      "captionsOn": "Sous-titres activés ({{label}}) : cliquez pour changer le style",
      "removeCaptions": "Retirer les sous-titres",
      "uploadSong": "Importer une chanson",
      "fileTypes": "MP3 · WAV · M4A · MP4 · jusqu'à {{mb}} Mo",
      "samples": "Chansons d'exemple",
      "mySongs": "Mes chansons",
      "signInSongs": "Connectez-vous pour utiliser vos chansons Solmi.",
      "loading": "Chargement…",
      "noSongs": "Pas encore de chanson.",
      "playPreview": "Lire l'aperçu",
      "pausePreview": "Mettre l'aperçu en pause",
      "removeSong": "Retirer la chanson",
      "upgrade1080": "Les exports 720p et 1080p sont réservés aux offres payantes. Passez à une offre supérieure pour les débloquer."
    }
  },
  zh: {
    "hero": {
      "h1": "把任何歌曲变成音乐视频。",
      "sub": "带上一首歌，选个格式，几次点击就能导演你的音乐视频。"
    },
    "picker": {
      "head": "选择格式",
      "music": "音乐视频",
      "musicSub": "叙事场景",
      "singing": "演唱视频",
      "singingSub": "对口型表演",
      "lyric": "歌词视频",
      "lyricSub": "歌词上屏",
      "song": "歌曲",
      "songSub": "仅音频",
      "mute": "静音演示",
      "soundOn": "有声播放演示"
    },
    "mine": {
      "projects": "我的项目",
      "assets": "我的文件"
    },
    "gallery": {
      "h2": "用 Solmi 制作的音乐视频",
      "browse": "浏览全部"
    },
    "hiw": {
      "h2": "工作原理",
      "step": "第 {{n}} 步",
      "play": "播放成片",
      "b1h": "添加音乐",
      "b1p": "粘贴链接、上传文件，或从资料库中选择。",
      "b1d": "链接 · 上传 · 资料库",
      "b2h": "分析与风格",
      "b2p": "我们读取速度、调性和段落，然后由你为这首歌挑一种导演风格。",
      "b2d": "BPM · 调性 · 段落 · 风格",
      "b3h": "角色与参考",
      "b3p": "角色、造型和场景——用你的照片，或我们推荐的表演者。",
      "b3d": "角色 · 造型 · 场景",
      "b4h": "分镜与渲染",
      "b4p": "每个镜头先出分镜；随意重新生成，然后渲染并下载。",
      "b4d": "画面 · 重新生成 · 时间线 · 下载",
      "c1": "粘贴链接、上传文件，或从资料库中选择——先有歌。",
      "c2": "我们读取速度、调性、段落和歌词，让每一个剪辑点都落在音乐上。",
      "c3": "导演为你这首歌给出的推荐——每种风格都决定了创意简报、参考图和分镜。",
      "c4": "角色、造型和场景被锁定为参考，让同一张脸出现在每个镜头里。",
      "c5": "渲染任何一帧之前，每个镜头都先出分镜；不满意的地方都可以重新生成。",
      "c6": "最终成片几分钟内渲染完成。在时间线上预览、下载，或分享链接。"
    },
    "faq": {
      "kicker": "快速解答",
      "h2": "常见问题",
      "q1": "制作一支音乐视频需要多久？",
      "a1": "免费的 5 秒预览约 20 秒渲染完成。分镜确认后，完整视频大约需要 3 分钟——你决定的时候不用排队等待。",
      "q2": "可以使用哪些音频？",
      "a2": "上传文件、从资料库中选择，或粘贴 TikTok 或 YouTube 链接。手头没有歌？先试试示例歌曲，看看 Solmi 能做什么。",
      "q3": "我需要拥有歌曲版权吗？",
      "a3": "凡是要发布的内容，需要——请使用你拥有或已获授权的音乐。用 Solmi 制作的视频属于你：你保留在这里创作内容的权利。",
      "q4": "可以导出哪些格式和分辨率？",
      "a4": "横屏 16:9 和竖屏 9:16，最高 1080p——每次导出都可选择添加字幕。",
      "q5": "有免费方案吗？",
      "a5": "你的第一支 5 秒视频免费，无需绑卡。渲染前会请你登录，以便结果保存到资料库。点数包和订阅可解锁完整时长的渲染。",
      "q6": "Solmi 支持歌词和其他语言吗？",
      "a6": "Solmi 读取速度、调性、段落和歌词来确定剪辑点，让剪辑贴合歌曲。创作者已用英语、西班牙语、法语、波兰语、罗马尼亚语和中文等制作了视频。"
    },
    "app": {
      "kicker": "iOS 和 Android",
      "h2": "把 Solmi 带在身边。",
      "p": "在 iPhone、iPad 和 Android 应用中创作歌曲、生成音乐视频、管理资料库并使用 Solmi 点数。",
      "storeSmall": "下载于",
      "storeBig": "App Store",
      "playSmall": "获取于",
      "playBig": "Google Play",
      "qrAria": "打开 Solmi 应用下载二维码链接",
      "qrAlt": "Solmi 应用下载页面二维码",
      "scan": "扫码下载",
      "qrNote": "二维码会打开 Solmi 下载页，并把手机引导到对应的应用商店。"
    },
    "close": {
      "h2a": "你的歌已经有了一个世界。",
      "h2b": "一起去找到它。",
      "p": "选个格式，带上一首歌，看前 5 秒渲染出来。"
    },
    "cta": {
      "music": "制作音乐视频",
      "short": "制作视频"
    },
    "dock": {
      "format": "格式",
      "notPicked": "尚未选择",
      "pickFormat": "选择格式",
      "song": "歌曲",
      "noSong": "还没有歌曲"
    },
    "footer": {
      "gallery": "画廊",
      "pricing": "价格",
      "blog": "博客",
      "discord": "Discord",
      "terms": "条款",
      "privacy": "隐私"
    },
    "stats": {
      "aria": "数据中的 Solmi",
      "creators": "全球创作者",
      "seconds": "已渲染视频秒数",
      "countries": "国家和地区"
    },
    "header": {
      "create": "创作",
      "myProjects": "我的项目",
      "login": "登录",
      "join": "免费加入 Solmi",
      "joinShort": "免费加入",
      "theme": "切换主题",
      "language": "语言",
      "menu": "菜单",
      "discord": "加入我们的 Discord",
      "rewards": "奖励",
      "shareEarn": "分享赚取",
      "inviteEarn": "邀请赚取",
      "affiliate": "联盟计划",
      "account": "账户",
      "member": "Solmi 用户",
      "myProjectsMenu": "我的项目"
    },
    "composer": {
      "placeholder": "上传歌曲或粘贴 {{platform}} 链接",
      "describe": "描述你想要的歌曲",
      "promptVideo": "描述你的视频（可选）",
      "creating": "创建中…",
      "fetching": "获取音频中…",
      "uploadingPct": "上传中 {{pct}}%",
      "uploadingAudio": "上传音频中…",
      "createLyric": "制作歌词视频",
      "makeSinging": "制作演唱视频",
      "createSong": "创作歌曲",
      "tooLarge": "音频文件不能超过 {{mb}}MB。",
      "uploaded": "音频已上传",
      "uploadFailed": "上传失败",
      "couldNotSave": "登录前无法保存该文件，请重试。",
      "invalidLink": "这看起来不是有效的链接。",
      "spotify": "Spotify 链接只提供 30 秒试听，因此不支持。请上传音频文件或粘贴 YouTube 链接。",
      "extracting": "正在从 {{platform}} 提取音频…",
      "attachedFrom": "已从 {{platform}} 添加",
      "couldNotExtract": "无法从该链接提取音频。",
      "privatePaid": "私密项目仅限付费方案。升级后即可将此项目设为私密。",
      "noAudio": "这首歌还没有音频。",
      "processing": "音频仍在处理中，请稍后再试。",
      "couldNotStart": "无法启动项目。",
      "untitled": "未命名",
      "untitledProject": "未命名项目",
      "reference": "参考图",
      "attachFirst": "选择一首歌或粘贴链接开始",
      "describeFirst": "请先描述你想要的歌曲",
      "selectSong": "选择歌曲",
      "images": "图片",
      "imagesOne": "1 张图片",
      "imagesCount": "{{count}} 张图片",
      "imagesTitle": "添加参考图片",
      "resolutionTitle": "导出分辨率",
      "publicProject": "公开项目",
      "privateProject": "私密项目",
      "publicTitle": "公开——会展示在 Solmi 画廊。点击设为私密。",
      "privateTitle": "私密——只有你能看到。点击设为公开。",
      "more": "更多",
      "aspect": "画面比例",
      "captions": "字幕",
      "captionsOff": "关闭",
      "captionsHint": "把带样式的歌词压制进导出的视频",
      "captionsOn": "字幕已开启（{{label}}）——点击更换样式",
      "removeCaptions": "移除字幕",
      "uploadSong": "上传歌曲",
      "fileTypes": "MP3 · WAV · M4A · MP4 · 最大 {{mb}}MB",
      "samples": "示例歌曲",
      "mySongs": "我的歌曲",
      "signInSongs": "登录后可使用你的 Solmi 歌曲。",
      "loading": "加载中…",
      "noSongs": "还没有歌曲。",
      "playPreview": "播放试听",
      "pausePreview": "暂停试听",
      "removeSong": "移除歌曲",
      "upgrade1080": "720p 和 1080p 导出仅限付费方案。升级后即可解锁。"
    }
  },
  ko: {
    "hero": {
      "h1": "어떤 노래든 뮤직비디오로.",
      "sub": "노래를 가져오고, 형식을 고르고, 몇 번의 클릭으로 뮤직비디오를 연출하세요."
    },
    "picker": {
      "head": "형식 선택",
      "music": "뮤직비디오",
      "musicSub": "내러티브 장면",
      "singing": "노래 영상",
      "singingSub": "립싱크 퍼포먼스",
      "lyric": "가사 영상",
      "lyricSub": "화면 위 가사",
      "song": "노래",
      "songSub": "오디오만",
      "mute": "데모 음소거",
      "soundOn": "소리와 함께 데모 재생"
    },
    "mine": {
      "projects": "내 프로젝트",
      "assets": "내 파일"
    },
    "gallery": {
      "h2": "Solmi로 만든 뮤직비디오",
      "browse": "전체 보기"
    },
    "hiw": {
      "h2": "작동 방식",
      "step": "{{n}}단계",
      "play": "완성된 영상 재생",
      "b1h": "음악 추가",
      "b1p": "링크를 붙여넣거나 파일을 업로드하거나 라이브러리에서 고르세요.",
      "b1d": "링크 · 업로드 · 라이브러리",
      "b2h": "분석 & 스타일",
      "b2p": "템포, 키, 구성을 읽은 뒤 곡에 맞는 감독 스타일을 고릅니다.",
      "b2d": "BPM · 키 · 구성 · 스타일",
      "b3h": "캐스팅 & 레퍼런스",
      "b3p": "캐릭터, 룩, 장소 — 내 사진이나 추천 퍼포머로.",
      "b3d": "캐릭터 · 룩 · 장소",
      "b4h": "스토리보드 & 렌더",
      "b4p": "모든 샷을 먼저 스토리보드로 확인하고, 원하는 부분을 다시 생성한 뒤 렌더링해 다운로드하세요.",
      "b4d": "프레임 · 재생성 · 타임라인 · 다운로드",
      "c1": "링크 붙여넣기, 파일 업로드, 라이브러리에서 선택 — 노래가 먼저입니다.",
      "c2": "템포, 키, 구성, 가사를 읽어 모든 컷이 음악에 정확히 맞도록 합니다.",
      "c3": "내 곡을 위한 감독의 추천 — 각 스타일이 브리프, 레퍼런스, 스토리보드를 이끕니다.",
      "c4": "캐릭터, 룩, 장소를 레퍼런스로 고정해 모든 샷에 같은 얼굴이 등장합니다.",
      "c5": "한 프레임도 렌더링하기 전에 모든 샷을 스토리보드로 만들고, 마음에 안 드는 부분은 다시 생성할 수 있습니다.",
      "c6": "최종 편집본은 몇 분 만에 렌더링됩니다. 타임라인에서 확인하고 다운로드하거나 링크를 공유하세요."
    },
    "faq": {
      "kicker": "빠른 답변",
      "h2": "자주 묻는 질문",
      "q1": "뮤직비디오를 만드는 데 얼마나 걸리나요?",
      "a1": "무료 5초 미리보기는 약 20초 만에 렌더링됩니다. 스토리보드를 승인하면 전체 영상은 약 3분이 걸리며, 결정하는 동안 대기열에서 기다릴 필요가 없습니다.",
      "q2": "어떤 오디오를 쓸 수 있나요?",
      "a2": "파일을 업로드하거나 라이브러리에서 고르거나 TikTok 또는 YouTube 링크를 붙여넣으세요. 곡이 없다면 샘플 곡으로 Solmi의 작동을 먼저 확인해 보세요.",
      "q3": "노래의 권리가 필요한가요?",
      "a3": "공개하는 모든 것에는 필요합니다. 본인 소유이거나 라이선스가 있는 음악을 사용하세요. Solmi로 만든 영상은 당신의 것이며, 여기서 만든 결과물의 권리는 당신에게 있습니다.",
      "q4": "어떤 형식과 해상도로 내보낼 수 있나요?",
      "a4": "가로 16:9와 세로 9:16, 최대 1080p — 모든 내보내기에 자막을 선택적으로 추가할 수 있습니다.",
      "q5": "무료 플랜이 있나요?",
      "a5": "첫 5초 영상은 무료이며 카드가 필요 없습니다. 렌더링 전에 로그인을 요청해 결과를 라이브러리에 저장합니다. 크레딧 팩과 구독으로 전체 길이 렌더링을 이용할 수 있습니다.",
      "q6": "가사와 다른 언어도 지원하나요?",
      "a6": "Solmi는 템포, 키, 구성, 가사를 읽어 컷 타이밍을 맞추므로 편집이 노래와 어우러집니다. 크리에이터들은 영어, 스페인어, 프랑스어, 폴란드어, 루마니아어, 중국어 등으로 영상을 만들었습니다."
    },
    "app": {
      "kicker": "iOS와 Android",
      "h2": "Solmi를 어디서나.",
      "p": "iPhone, iPad, Android 앱에서 노래를 만들고, 뮤직비디오를 생성하고, 라이브러리를 관리하고, Solmi 크레딧을 사용하세요.",
      "storeSmall": "다운로드",
      "storeBig": "App Store",
      "playSmall": "다운로드",
      "playBig": "Google Play",
      "qrAria": "Solmi 앱 다운로드 QR 링크 열기",
      "qrAlt": "Solmi 앱 다운로드 페이지 QR 코드",
      "scan": "스캔하여 다운로드",
      "qrNote": "QR을 스캔하면 기기에 맞는 스토어로 안내하는 Solmi 다운로드 페이지가 열립니다."
    },
    "close": {
      "h2a": "당신의 곡에는 이미 세계가 있습니다.",
      "h2b": "함께 찾아봐요.",
      "p": "형식을 고르고 노래를 가져오면 첫 5초가 렌더링되는 것을 볼 수 있습니다."
    },
    "cta": {
      "music": "뮤직비디오 만들기",
      "short": "영상 만들기"
    },
    "dock": {
      "format": "형식",
      "notPicked": "아직 선택 안 함",
      "pickFormat": "형식 선택",
      "song": "노래",
      "noSong": "아직 노래 없음"
    },
    "footer": {
      "gallery": "갤러리",
      "pricing": "요금",
      "blog": "블로그",
      "discord": "Discord",
      "terms": "이용약관",
      "privacy": "개인정보"
    },
    "stats": {
      "aria": "숫자로 보는 Solmi",
      "creators": "전 세계 크리에이터",
      "seconds": "렌더링된 영상(초)",
      "countries": "국가 및 지역"
    },
    "header": {
      "create": "만들기",
      "myProjects": "내 프로젝트",
      "login": "로그인",
      "join": "무료로 Solmi 시작",
      "joinShort": "무료 시작",
      "theme": "테마 전환",
      "language": "언어",
      "menu": "메뉴",
      "discord": "Discord 참여",
      "rewards": "리워드",
      "shareEarn": "공유하고 받기",
      "inviteEarn": "초대하고 받기",
      "affiliate": "제휴 프로그램",
      "account": "계정",
      "member": "Solmi 회원",
      "myProjectsMenu": "내 프로젝트"
    },
    "composer": {
      "placeholder": "노래를 업로드하거나 {{platform}} 링크를 붙여넣으세요",
      "describe": "원하는 노래를 설명하세요",
      "promptVideo": "영상을 설명하세요 (선택)",
      "creating": "만드는 중…",
      "fetching": "오디오 가져오는 중…",
      "uploadingPct": "업로드 중 {{pct}}%",
      "uploadingAudio": "오디오 업로드 중…",
      "createLyric": "가사 영상 만들기",
      "makeSinging": "노래 영상 만들기",
      "createSong": "노래 만들기",
      "tooLarge": "오디오 파일은 {{mb}}MB 이하여야 합니다.",
      "uploaded": "오디오 업로드 완료",
      "uploadFailed": "업로드 실패",
      "couldNotSave": "로그인 전에 파일을 저장하지 못했습니다. 다시 시도해 주세요.",
      "invalidLink": "유효한 링크가 아닌 것 같습니다.",
      "spotify": "Spotify 링크는 30초 미리듣기만 제공하므로 지원되지 않습니다. 오디오 파일을 업로드하거나 YouTube 링크를 붙여넣으세요.",
      "extracting": "{{platform}}에서 오디오 추출 중…",
      "attachedFrom": "{{platform}}에서 추가됨",
      "couldNotExtract": "해당 링크에서 오디오를 추출할 수 없습니다.",
      "privatePaid": "비공개 프로젝트는 유료 플랜에서 이용할 수 있습니다. 업그레이드하여 비공개로 설정하세요.",
      "noAudio": "이 트랙에는 아직 오디오가 없습니다.",
      "processing": "오디오를 아직 처리 중입니다. 잠시 후 다시 시도하세요.",
      "couldNotStart": "프로젝트를 시작할 수 없습니다.",
      "untitled": "제목 없음",
      "untitledProject": "제목 없는 프로젝트",
      "reference": "레퍼런스",
      "attachFirst": "노래를 선택하거나 링크를 붙여넣어 시작하세요",
      "describeFirst": "먼저 원하는 노래를 설명하세요",
      "selectSong": "노래 선택",
      "images": "이미지",
      "imagesOne": "이미지 1개",
      "imagesCount": "이미지 {{count}}개",
      "imagesTitle": "레퍼런스 이미지 추가",
      "resolutionTitle": "내보내기 해상도",
      "publicProject": "공개 프로젝트",
      "privateProject": "비공개 프로젝트",
      "publicTitle": "공개 — Solmi 갤러리에 표시됩니다. 클릭하여 비공개로 전환.",
      "privateTitle": "비공개 — 나만 볼 수 있습니다. 클릭하여 공개로 전환.",
      "more": "더 보기",
      "aspect": "화면 비율",
      "captions": "자막",
      "captionsOff": "끔",
      "captionsHint": "스타일이 적용된 가사를 내보내는 영상에 새깁니다",
      "captionsOn": "자막 켬({{label}}) — 클릭하여 스타일 변경",
      "removeCaptions": "자막 제거",
      "uploadSong": "노래 업로드",
      "fileTypes": "MP3 · WAV · M4A · MP4 · 최대 {{mb}}MB",
      "samples": "샘플 곡",
      "mySongs": "내 노래",
      "signInSongs": "로그인하면 내 Solmi 노래를 사용할 수 있습니다.",
      "loading": "불러오는 중…",
      "noSongs": "아직 노래가 없습니다.",
      "playPreview": "미리듣기 재생",
      "pausePreview": "미리듣기 일시정지",
      "removeSong": "노래 제거",
      "upgrade1080": "720p 및 1080p 내보내기는 유료 플랜에서 이용할 수 있습니다. 업그레이드하여 잠금 해제하세요."
    }
  },
  id: {
    "hero": {
      "h1": "Ubah lagu apa pun jadi video musik.",
      "sub": "Bawa lagu. Pilih format. Sutradarai video musikmu dalam beberapa klik."
    },
    "picker": {
      "head": "Pilih format",
      "music": "Video musik",
      "musicSub": "Adegan naratif",
      "singing": "Video bernyanyi",
      "singingSub": "Penampilan lip-sync",
      "lyric": "Video lirik",
      "lyricSub": "Lirik di layar",
      "song": "Lagu",
      "songSub": "Hanya audio",
      "mute": "Bisukan demo",
      "soundOn": "Putar demo dengan suara"
    },
    "mine": {
      "projects": "Proyek saya",
      "assets": "Berkas saya"
    },
    "gallery": {
      "h2": "Video musik yang dibuat dengan Solmi",
      "browse": "Lihat semua"
    },
    "hiw": {
      "h2": "Cara kerjanya",
      "step": "Langkah {{n}}",
      "play": "Putar video jadi",
      "b1h": "Tambahkan musik",
      "b1p": "Tempel tautan, unggah berkas, atau pilih dari pustaka.",
      "b1d": "Tautan · Unggah · Pustaka",
      "b2h": "Analisis & gaya",
      "b2p": "Kami membaca tempo, nada, dan bagian lagu, lalu kamu memilih gaya sutradara untuk lagunya.",
      "b2d": "BPM · Nada · Bagian · Gaya",
      "b3h": "Pemeran & referensi",
      "b3p": "Karakter, tampilan, dan lokasi — fotomu atau penampil yang kami sarankan.",
      "b3d": "Karakter · Tampilan · Lokasi",
      "b4h": "Storyboard & render",
      "b4p": "Setiap adegan digambar dulu; buat ulang apa pun, lalu render dan unduh.",
      "b4d": "Frame · Buat ulang · Linimasa · Unduh",
      "c1": "Tempel tautan, unggah berkas, atau pilih dari pustaka — lagu dulu.",
      "c2": "Kami membaca tempo, nada, bagian, dan lirik agar setiap potongan jatuh pas di musik.",
      "c3": "Pilihan sutradara untuk lagumu — setiap gaya mengarahkan brief, referensi, dan storyboard.",
      "c4": "Karakter, tampilan, dan lokasi dikunci sebagai referensi, sehingga wajah yang sama muncul di setiap adegan.",
      "c5": "Setiap adegan digambar sebelum satu frame pun dirender, dan apa pun yang tidak kamu suka bisa dibuat ulang.",
      "c6": "Hasil akhir dirender dalam hitungan menit. Tonton di linimasa, unduh, atau bagikan tautannya."
    },
    "faq": {
      "kicker": "Jawaban singkat",
      "h2": "Pertanyaan umum.",
      "q1": "Berapa lama membuat video musik?",
      "a1": "Pratinjau gratis lima detik dirender sekitar dua puluh detik. Video penuh butuh sekitar tiga menit setelah storyboard disetujui — tidak perlu mengantre saat kamu memutuskan.",
      "q2": "Audio apa yang bisa saya pakai?",
      "a2": "Unggah berkas, pilih dari pustaka, atau tempel tautan TikTok atau YouTube. Kalau belum punya lagu, coba salah satu lagu contoh untuk melihat apa yang Solmi lakukan.",
      "q3": "Apakah saya butuh hak atas lagunya?",
      "a3": "Untuk semua yang kamu publikasikan, ya — gunakan musik milikmu atau yang berlisensi. Video yang kamu buat dengan Solmi adalah milikmu: hak atas karya yang kamu buat di sini tetap milikmu.",
      "q4": "Format dan resolusi apa yang bisa diekspor?",
      "a4": "Lanskap 16:9 dan vertikal 9:16, hingga 1080p — dengan takarir opsional di setiap ekspor.",
      "q5": "Ada paket gratis?",
      "a5": "Video lima detik pertamamu gratis tanpa kartu. Kamu diminta masuk sebelum render agar hasilnya tersimpan di pustakamu. Paket kredit dan langganan membuka render penuh.",
      "q6": "Apakah Solmi mendukung lirik dan bahasa lain?",
      "a6": "Solmi membaca tempo, nada, bagian, dan lirik untuk mengatur waktu potongan — editnya mengikuti lagu. Kreator sudah membuat video dalam bahasa Inggris, Spanyol, Prancis, Polandia, Rumania, dan Mandarin, antara lain."
    },
    "app": {
      "kicker": "iOS dan Android",
      "h2": "Bawa Solmi ke mana saja.",
      "p": "Buat lagu, hasilkan video musik, kelola pustaka, dan gunakan kredit Solmi dari aplikasi iPhone, iPad, dan Android.",
      "storeSmall": "Unduh di",
      "storeBig": "App Store",
      "playSmall": "Dapatkan di",
      "playBig": "Google Play",
      "qrAria": "Buka tautan QR unduhan aplikasi Solmi",
      "qrAlt": "Kode QR halaman unduhan aplikasi Solmi",
      "scan": "Pindai untuk mengunduh",
      "qrNote": "QR membuka halaman unduhan Solmi yang mengarahkan ponsel ke toko yang tepat."
    },
    "close": {
      "h2a": "Lagumu sudah punya dunianya.",
      "h2b": "Ayo kita temukan.",
      "p": "Pilih format, bawa lagu, dan lihat lima detik pertama dirender."
    },
    "cta": {
      "music": "Buat video musik",
      "short": "Buat video"
    },
    "dock": {
      "format": "Format",
      "notPicked": "Belum dipilih",
      "pickFormat": "Pilih format",
      "song": "Lagu",
      "noSong": "Belum ada lagu"
    },
    "footer": {
      "gallery": "Galeri",
      "pricing": "Harga",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Ketentuan",
      "privacy": "Privasi"
    },
    "stats": {
      "aria": "Solmi dalam angka",
      "creators": "Kreator di seluruh dunia",
      "seconds": "Detik video dirender",
      "countries": "Negara & wilayah"
    },
    "header": {
      "create": "Buat",
      "myProjects": "Proyek saya",
      "login": "Masuk",
      "join": "Gabung Solmi gratis",
      "joinShort": "Gabung gratis",
      "theme": "Ganti tema",
      "language": "Bahasa",
      "menu": "Menu",
      "discord": "Gabung Discord kami",
      "rewards": "Hadiah",
      "shareEarn": "Bagikan & dapatkan",
      "inviteEarn": "Undang & dapatkan",
      "affiliate": "Program afiliasi",
      "account": "Akun",
      "member": "Anggota Solmi",
      "myProjectsMenu": "Proyek saya"
    },
    "composer": {
      "placeholder": "Unggah lagu atau tempel tautan {{platform}}",
      "describe": "Jelaskan lagu yang kamu inginkan",
      "promptVideo": "Deskripsikan videomu (opsional)",
      "creating": "Membuat…",
      "fetching": "Mengambil audio…",
      "uploadingPct": "Mengunggah {{pct}}%",
      "uploadingAudio": "Mengunggah audio…",
      "createLyric": "Buat video lirik",
      "makeSinging": "Buat video bernyanyi",
      "createSong": "Buat lagu",
      "tooLarge": "Berkas audio maksimal {{mb}}MB.",
      "uploaded": "Audio terunggah",
      "uploadFailed": "Unggahan gagal",
      "couldNotSave": "Berkas ini tidak bisa disimpan sebelum masuk. Coba lagi.",
      "invalidLink": "Itu tidak terlihat seperti tautan yang valid.",
      "spotify": "Tautan Spotify hanya memberi pratinjau 30 detik, jadi tidak didukung. Unggah berkas audio atau tempel tautan YouTube.",
      "extracting": "Mengekstrak audio dari {{platform}}…",
      "attachedFrom": "Ditambahkan dari {{platform}}",
      "couldNotExtract": "Tidak bisa mengekstrak audio dari tautan itu.",
      "privatePaid": "Proyek privat tersedia di paket berbayar. Upgrade untuk menjadikan proyek ini privat.",
      "noAudio": "Lagu ini belum punya audio.",
      "processing": "Audio masih diproses — coba lagi sebentar lagi.",
      "couldNotStart": "Proyek tidak bisa dimulai.",
      "untitled": "Tanpa judul",
      "untitledProject": "Proyek tanpa judul",
      "reference": "Referensi",
      "attachFirst": "Pilih lagu atau tempel tautan untuk memulai",
      "describeFirst": "Jelaskan dulu lagu yang kamu inginkan",
      "selectSong": "Pilih lagu",
      "images": "Gambar",
      "imagesOne": "1 gambar",
      "imagesCount": "{{count}} gambar",
      "imagesTitle": "Tambah gambar referensi",
      "resolutionTitle": "Resolusi ekspor",
      "publicProject": "Proyek publik",
      "privateProject": "Proyek privat",
      "publicTitle": "Publik — tampil di galeri Solmi. Klik untuk menjadikan privat.",
      "privateTitle": "Privat — hanya kamu yang bisa melihat. Klik untuk menjadikan publik.",
      "more": "Lainnya",
      "aspect": "Rasio aspek",
      "captions": "Takarir",
      "captionsOff": "Mati",
      "captionsHint": "Bakar lirik bergaya ke video yang diekspor",
      "captionsOn": "Takarir aktif ({{label}}) — klik untuk mengubah gaya",
      "removeCaptions": "Hapus takarir",
      "uploadSong": "Unggah lagu",
      "fileTypes": "MP3 · WAV · M4A · MP4 · hingga {{mb}}MB",
      "samples": "Lagu contoh",
      "mySongs": "Lagu saya",
      "signInSongs": "Masuk untuk memakai lagu Solmi-mu.",
      "loading": "Memuat…",
      "noSongs": "Belum ada lagu.",
      "playPreview": "Putar pratinjau",
      "pausePreview": "Jeda pratinjau",
      "removeSong": "Hapus lagu",
      "upgrade1080": "Ekspor 720p dan 1080p tersedia di paket berbayar. Upgrade untuk membukanya."
    }
  },
  it: {
    "hero": {
      "h1": "Trasforma qualsiasi canzone in un video musicale.",
      "sub": "Porta una canzone. Scegli un formato. Dirigi il tuo video in pochi clic."
    },
    "picker": {
      "head": "Scegli un formato",
      "music": "Video musicale",
      "musicSub": "Scene narrative",
      "singing": "Video cantato",
      "singingSub": "Performance in lip-sync",
      "lyric": "Lyric video",
      "lyricSub": "Testo sullo schermo",
      "song": "Canzone",
      "songSub": "Solo audio",
      "mute": "Silenzia la demo",
      "soundOn": "Riproduci la demo con l'audio"
    },
    "mine": {
      "projects": "I miei progetti",
      "assets": "I miei file"
    },
    "gallery": {
      "h2": "Video musicali creati con Solmi",
      "browse": "Vedi tutti"
    },
    "hiw": {
      "h2": "Come funziona",
      "step": "Passo {{n}}",
      "play": "Guarda il video finale",
      "b1h": "Aggiungi la musica",
      "b1p": "Incolla un link, carica un file o scegli dalla tua libreria.",
      "b1d": "Link · Upload · Libreria",
      "b2h": "Analisi e stile",
      "b2p": "Leggiamo tempo, tonalità e sezioni, poi scegli uno stile da regista per il brano.",
      "b2d": "BPM · Tonalità · Sezioni · Stili",
      "b3h": "Cast e riferimenti",
      "b3p": "Personaggio, look e location: la tua foto o un interprete che ti suggeriamo.",
      "b3d": "Personaggio · Look · Location",
      "b4h": "Storyboard e render",
      "b4p": "Ogni inquadratura viene prima disegnata; rigenera ciò che vuoi, poi renderizza e scarica.",
      "b4d": "Inquadrature · Rigenera · Timeline · Download",
      "c1": "Incolla un link, carica un file o scegli dalla tua libreria: prima di tutto la canzone.",
      "c2": "Leggiamo tempo, tonalità, sezioni e testo, così ogni taglio cade sulla musica.",
      "c3": "Le proposte del regista per il tuo brano: ogni stile guida il brief, i riferimenti e lo storyboard.",
      "c4": "Personaggio, look e location vengono fissati come riferimenti, così lo stesso volto appare in ogni inquadratura.",
      "c5": "Ogni inquadratura viene disegnata prima di renderizzare un solo fotogramma, e tutto ciò che non ti piace si può rigenerare.",
      "c6": "Il montaggio finale si renderizza in pochi minuti. Guardalo sulla timeline, scaricalo o condividi il link."
    },
    "faq": {
      "kicker": "Risposte rapide",
      "h2": "Domande frequenti.",
      "q1": "Quanto ci vuole per fare un video musicale?",
      "a1": "L'anteprima gratuita di cinque secondi si renderizza in circa venti secondi. Un video completo richiede circa tre minuti dopo l'approvazione dello storyboard: nessuna coda mentre decidi.",
      "q2": "Quale audio posso usare?",
      "a2": "Carica un file, scegli dalla tua libreria o incolla un link TikTok o YouTube. Se non hai un brano a portata di mano, prova una delle canzoni di esempio per vedere cosa fa Solmi prima di impegnarti.",
      "q3": "Servono i diritti della canzone?",
      "a3": "Per tutto ciò che pubblichi, sì: usa musica tua o con licenza. I video che crei con Solmi sono tuoi: mantieni i diritti su ciò che crei qui.",
      "q4": "Quali formati e risoluzioni posso esportare?",
      "a4": "Orizzontale 16:9 e verticale 9:16, fino a 1080p, con sottotitoli opzionali in ogni esportazione.",
      "q5": "Esiste un piano gratuito?",
      "a5": "Il tuo primo video di cinque secondi è gratis e senza carta. Ti chiederemo di accedere prima del render, così il risultato viene salvato nella tua libreria. Pacchetti di crediti e abbonamenti sbloccano i render completi.",
      "q6": "Solmi gestisce testi e altre lingue?",
      "a6": "Solmi legge tempo, tonalità, sezioni e testo e li usa per sincronizzare i tagli: il montaggio segue la canzone. I creator hanno realizzato video in inglese, spagnolo, francese, polacco, rumeno e cinese, tra gli altri."
    },
    "app": {
      "kicker": "iOS e Android",
      "h2": "Porta Solmi con te.",
      "p": "Crea canzoni, genera video musicali, gestisci la tua libreria e usa i tuoi crediti Solmi dall'app per iPhone, iPad e Android.",
      "storeSmall": "Scarica su",
      "storeBig": "App Store",
      "playSmall": "Disponibile su",
      "playBig": "Google Play",
      "qrAria": "Apri il link QR per scaricare l'app Solmi",
      "qrAlt": "Codice QR della pagina di download dell'app Solmi",
      "scan": "Scansiona per scaricare",
      "qrNote": "Il QR apre una pagina di download Solmi che porta ogni telefono allo store giusto."
    },
    "close": {
      "h2a": "Il tuo brano ha già un mondo.",
      "h2b": "Andiamo a trovarlo.",
      "p": "Scegli un formato, porta una canzone e guarda i primi cinque secondi renderizzarsi."
    },
    "cta": {
      "music": "Crea video musicale",
      "short": "Crea video"
    },
    "dock": {
      "format": "Formato",
      "notPicked": "Non ancora scelto",
      "pickFormat": "Scegli un formato",
      "song": "Canzone",
      "noSong": "Nessuna canzone"
    },
    "footer": {
      "gallery": "Galleria",
      "pricing": "Prezzi",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Termini",
      "privacy": "Privacy"
    },
    "stats": {
      "aria": "Solmi in numeri",
      "creators": "Creator nel mondo",
      "seconds": "Secondi di video renderizzati",
      "countries": "Paesi e regioni"
    },
    "header": {
      "create": "Crea",
      "myProjects": "I miei progetti",
      "login": "Accedi",
      "join": "Unisciti a Solmi gratis",
      "joinShort": "Unisciti gratis",
      "theme": "Cambia tema",
      "language": "Lingua",
      "menu": "Menu",
      "discord": "Unisciti al nostro Discord",
      "rewards": "Premi",
      "shareEarn": "Condividi e guadagna",
      "inviteEarn": "Invita e guadagna",
      "affiliate": "Programma di affiliazione",
      "account": "Account",
      "member": "Membro Solmi",
      "myProjectsMenu": "I miei progetti"
    },
    "composer": {
      "placeholder": "Carica una canzone o incolla un link {{platform}}",
      "describe": "Descrivi la canzone che vuoi",
      "promptVideo": "Descrivi il tuo video (facoltativo)",
      "creating": "Creazione…",
      "fetching": "Recupero audio…",
      "uploadingPct": "Caricamento {{pct}}%",
      "uploadingAudio": "Caricamento audio…",
      "createLyric": "Crea lyric video",
      "makeSinging": "Crea video cantato",
      "createSong": "Crea canzone",
      "tooLarge": "I file audio devono essere di massimo {{mb}}MB.",
      "uploaded": "Audio caricato",
      "uploadFailed": "Caricamento non riuscito",
      "couldNotSave": "Impossibile salvare il file prima dell'accesso. Riprova.",
      "invalidLink": "Non sembra un link valido.",
      "spotify": "I link Spotify offrono solo un'anteprima di 30 secondi e non sono supportati. Carica il file audio o incolla un link YouTube.",
      "extracting": "Estrazione audio da {{platform}}…",
      "attachedFrom": "Aggiunto da {{platform}}",
      "couldNotExtract": "Impossibile estrarre l'audio da quel link.",
      "privatePaid": "I progetti privati sono disponibili nei piani a pagamento. Passa a un piano superiore per renderlo privato.",
      "noAudio": "Questo brano non ha ancora audio.",
      "processing": "Audio ancora in elaborazione: riprova tra un momento.",
      "couldNotStart": "Impossibile avviare il progetto.",
      "untitled": "Senza titolo",
      "untitledProject": "Progetto senza titolo",
      "reference": "Riferimento",
      "attachFirst": "Scegli una canzone o incolla un link per iniziare",
      "describeFirst": "Descrivi prima la canzone che vuoi",
      "selectSong": "Scegli una canzone",
      "images": "Immagini",
      "imagesOne": "1 immagine",
      "imagesCount": "{{count}} immagini",
      "imagesTitle": "Aggiungi immagini di riferimento",
      "resolutionTitle": "Risoluzione di esportazione",
      "publicProject": "Progetto pubblico",
      "privateProject": "Progetto privato",
      "publicTitle": "Pubblico: visibile nella galleria Solmi. Clicca per renderlo privato.",
      "privateTitle": "Privato: solo tu puoi vederlo. Clicca per renderlo pubblico.",
      "more": "Altro",
      "aspect": "Proporzioni",
      "captions": "Sottotitoli",
      "captionsOff": "Disattivati",
      "captionsHint": "Imprimi il testo stilizzato nel video esportato",
      "captionsOn": "Sottotitoli attivi ({{label}}): clicca per cambiare stile",
      "removeCaptions": "Rimuovi sottotitoli",
      "uploadSong": "Carica una canzone",
      "fileTypes": "MP3 · WAV · M4A · MP4 · fino a {{mb}}MB",
      "samples": "Canzoni di esempio",
      "mySongs": "Le mie canzoni",
      "signInSongs": "Accedi per usare le tue canzoni Solmi.",
      "loading": "Caricamento…",
      "noSongs": "Ancora nessuna canzone.",
      "playPreview": "Riproduci anteprima",
      "pausePreview": "Metti in pausa l'anteprima",
      "removeSong": "Rimuovi canzone",
      "upgrade1080": "Le esportazioni in 720p e 1080p sono disponibili nei piani a pagamento. Passa a un piano superiore per sbloccarle."
    }
  },
  nl: {
    "hero": {
      "h1": "Maak van elk nummer een muziekvideo.",
      "sub": "Breng een nummer mee. Kies een formaat. Regisseer je muziekvideo in een paar klikken."
    },
    "picker": {
      "head": "Kies een formaat",
      "music": "Muziekvideo",
      "musicSub": "Verhalende scènes",
      "singing": "Zangvideo",
      "singingSub": "Lipsync-optreden",
      "lyric": "Lyricvideo",
      "lyricSub": "Tekst in beeld",
      "song": "Nummer",
      "songSub": "Alleen audio",
      "mute": "Demo dempen",
      "soundOn": "Demo met geluid afspelen"
    },
    "mine": {
      "projects": "Mijn projecten",
      "assets": "Mijn bestanden"
    },
    "gallery": {
      "h2": "Muziekvideo's gemaakt met Solmi",
      "browse": "Alles bekijken"
    },
    "hiw": {
      "h2": "Zo werkt het",
      "step": "Stap {{n}}",
      "play": "De afgewerkte video afspelen",
      "b1h": "Muziek toevoegen",
      "b1p": "Plak een link, upload een bestand of kies uit je bibliotheek.",
      "b1d": "Link · Upload · Bibliotheek",
      "b2h": "Analyse & stijl",
      "b2p": "We lezen tempo, toonsoort en secties; daarna kies je een regisseursstijl voor het nummer.",
      "b2d": "BPM · Toonsoort · Secties · Stijlen",
      "b3h": "Cast & referenties",
      "b3p": "Personage, look en locatie: jouw foto of een artiest die wij voorstellen.",
      "b3d": "Personage · Look · Locatie",
      "b4h": "Storyboard & render",
      "b4p": "Elk shot wordt eerst uitgetekend; genereer opnieuw wat je wilt, render en download.",
      "b4d": "Frames · Opnieuw genereren · Tijdlijn · Downloaden",
      "c1": "Plak een link, upload een bestand of kies uit je bibliotheek — het nummer komt eerst.",
      "c2": "We lezen tempo, toonsoort, secties en tekst, zodat elke cut op de muziek valt.",
      "c3": "De keuzes van de regisseur voor jouw nummer — elke stijl stuurt de briefing, de referenties en het storyboard.",
      "c4": "Personage, look en locatie worden vastgelegd als referenties, zodat hetzelfde gezicht in elk shot verschijnt.",
      "c5": "Elk shot wordt uitgetekend voordat er één frame wordt gerenderd, en alles wat je niet bevalt kun je opnieuw genereren.",
      "c6": "De definitieve cut rendert in minuten. Bekijk hem op de tijdlijn, download hem of deel de link."
    },
    "faq": {
      "kicker": "Snelle antwoorden",
      "h2": "Veelgestelde vragen.",
      "q1": "Hoe lang duurt het om een muziekvideo te maken?",
      "a1": "De gratis voorvertoning van vijf seconden rendert in zo'n twintig seconden. Een volledige video duurt ongeveer drie minuten zodra je het storyboard hebt goedgekeurd — je wacht niet in een rij terwijl je beslist.",
      "q2": "Welke audio kan ik gebruiken?",
      "a2": "Upload een bestand, kies iets uit je bibliotheek of plak een TikTok- of YouTube-link. Heb je geen nummer bij de hand, probeer dan een van de voorbeeldnummers om te zien wat Solmi doet.",
      "q3": "Heb ik de rechten op het nummer nodig?",
      "a3": "Voor alles wat je publiceert wel — gebruik muziek die van jou is of gelicentieerd. Video's die je met Solmi maakt zijn van jou: je behoudt de rechten op wat je hier maakt.",
      "q4": "Welke formaten en resoluties kan ik exporteren?",
      "a4": "Liggend 16:9 en staand 9:16, tot 1080p — met optionele ondertitels bij elke export.",
      "q5": "Is er een gratis abonnement?",
      "a5": "Je eerste video van vijf seconden is gratis, zonder kaart. Je wordt gevraagd in te loggen vóór het renderen, zodat het resultaat in je bibliotheek wordt bewaard. Creditpakketten en abonnementen ontgrendelen volledige renders.",
      "q6": "Werkt Solmi met songteksten en andere talen?",
      "a6": "Solmi leest tempo, toonsoort, secties en tekst en gebruikt die om de cuts te timen — de montage volgt het nummer. Makers hebben video's gemaakt in onder meer Engels, Spaans, Frans, Pools, Roemeens en Chinees."
    },
    "app": {
      "kicker": "iOS en Android",
      "h2": "Neem Solmi mee.",
      "p": "Maak nummers, genereer muziekvideo's, beheer je bibliotheek en gebruik je Solmi-credits in de app voor iPhone, iPad en Android.",
      "storeSmall": "Download in de",
      "storeBig": "App Store",
      "playSmall": "Ontdek het op",
      "playBig": "Google Play",
      "qrAria": "QR-link openen om de Solmi-app te downloaden",
      "qrAlt": "QR-code van de downloadpagina van de Solmi-app",
      "scan": "Scan om te downloaden",
      "qrNote": "De QR opent een Solmi-downloadpagina die elke telefoon naar de juiste store stuurt."
    },
    "close": {
      "h2a": "Je nummer heeft al een wereld.",
      "h2b": "Laten we die vinden.",
      "p": "Kies een formaat, breng een nummer mee en zie de eerste vijf seconden renderen."
    },
    "cta": {
      "music": "Muziekvideo maken",
      "short": "Video maken"
    },
    "dock": {
      "format": "Formaat",
      "notPicked": "Nog niet gekozen",
      "pickFormat": "Kies een formaat",
      "song": "Nummer",
      "noSong": "Nog geen nummer"
    },
    "footer": {
      "gallery": "Galerij",
      "pricing": "Prijzen",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Voorwaarden",
      "privacy": "Privacy"
    },
    "stats": {
      "aria": "Solmi in cijfers",
      "creators": "Makers wereldwijd",
      "seconds": "Seconden gerenderde video",
      "countries": "Landen en regio's"
    },
    "header": {
      "create": "Maken",
      "myProjects": "Mijn projecten",
      "login": "Inloggen",
      "join": "Gratis lid worden",
      "joinShort": "Gratis starten",
      "theme": "Thema wisselen",
      "language": "Taal",
      "menu": "Menu",
      "discord": "Kom bij onze Discord",
      "rewards": "Beloningen",
      "shareEarn": "Deel & verdien",
      "inviteEarn": "Nodig uit & verdien",
      "affiliate": "Partnerprogramma",
      "account": "Account",
      "member": "Solmi-lid",
      "myProjectsMenu": "Mijn projecten"
    },
    "composer": {
      "placeholder": "Upload een nummer of plak een {{platform}}-link",
      "describe": "Beschrijf het nummer dat je wilt",
      "promptVideo": "Beschrijf je video (optioneel)",
      "creating": "Bezig met maken…",
      "fetching": "Audio ophalen…",
      "uploadingPct": "Uploaden {{pct}}%",
      "uploadingAudio": "Audio uploaden…",
      "createLyric": "Lyricvideo maken",
      "makeSinging": "Zangvideo maken",
      "createSong": "Nummer maken",
      "tooLarge": "Audiobestanden mogen maximaal {{mb}}MB zijn.",
      "uploaded": "Audio geüpload",
      "uploadFailed": "Upload mislukt",
      "couldNotSave": "Dit bestand kon niet worden bewaard vóór het inloggen. Probeer opnieuw.",
      "invalidLink": "Dat lijkt geen geldige link.",
      "spotify": "Spotify-links bieden alleen een voorvertoning van 30 seconden en worden niet ondersteund. Upload het audiobestand of plak een YouTube-link.",
      "extracting": "Audio ophalen van {{platform}}…",
      "attachedFrom": "Toegevoegd van {{platform}}",
      "couldNotExtract": "Kon geen audio uit die link halen.",
      "privatePaid": "Privéprojecten zijn beschikbaar in betaalde abonnementen. Upgrade om dit project privé te maken.",
      "noAudio": "Dit nummer heeft nog geen audio.",
      "processing": "Die audio wordt nog verwerkt — probeer het zo opnieuw.",
      "couldNotStart": "Het project kon niet worden gestart.",
      "untitled": "Naamloos",
      "untitledProject": "Naamloos project",
      "reference": "Referentie",
      "attachFirst": "Kies een nummer of plak een link om te beginnen",
      "describeFirst": "Beschrijf eerst het nummer dat je wilt",
      "selectSong": "Kies een nummer",
      "images": "Afbeeldingen",
      "imagesOne": "1 afbeelding",
      "imagesCount": "{{count}} afbeeldingen",
      "imagesTitle": "Referentieafbeeldingen toevoegen",
      "resolutionTitle": "Exportresolutie",
      "publicProject": "Openbaar project",
      "privateProject": "Privéproject",
      "publicTitle": "Openbaar — zichtbaar in de Solmi-galerij. Klik om privé te maken.",
      "privateTitle": "Privé — alleen jij ziet het. Klik om openbaar te maken.",
      "more": "Meer",
      "aspect": "Beeldverhouding",
      "captions": "Ondertitels",
      "captionsOff": "Uit",
      "captionsHint": "Gestileerde songtekst in de geëxporteerde video branden",
      "captionsOn": "Ondertitels aan ({{label}}) — klik om de stijl te wijzigen",
      "removeCaptions": "Ondertitels verwijderen",
      "uploadSong": "Een nummer uploaden",
      "fileTypes": "MP3 · WAV · M4A · MP4 · tot {{mb}}MB",
      "samples": "Voorbeeldnummers",
      "mySongs": "Mijn nummers",
      "signInSongs": "Log in om je Solmi-nummers te gebruiken.",
      "loading": "Laden…",
      "noSongs": "Nog geen nummers.",
      "playPreview": "Voorbeeld afspelen",
      "pausePreview": "Voorbeeld pauzeren",
      "removeSong": "Nummer verwijderen",
      "upgrade1080": "Exports in 720p en 1080p zijn beschikbaar in betaalde abonnementen. Upgrade om ze te ontgrendelen."
    }
  },
  pl: {
    "hero": {
      "h1": "Zamień dowolny utwór w teledysk.",
      "sub": "Dodaj utwór. Wybierz format. Wyreżyseruj teledysk w kilku kliknięciach."
    },
    "picker": {
      "head": "Wybierz format",
      "music": "Teledysk",
      "musicSub": "Sceny fabularne",
      "singing": "Wideo ze śpiewem",
      "singingSub": "Występ z synchronizacją ust",
      "lyric": "Wideo z tekstem",
      "lyricSub": "Słowa na ekranie",
      "song": "Utwór",
      "songSub": "Tylko audio",
      "mute": "Wycisz demo",
      "soundOn": "Odtwórz demo z dźwiękiem"
    },
    "mine": {
      "projects": "Moje projekty",
      "assets": "Moje pliki"
    },
    "gallery": {
      "h2": "Teledyski stworzone w Solmi",
      "browse": "Zobacz wszystkie"
    },
    "hiw": {
      "h2": "Jak to działa",
      "step": "Krok {{n}}",
      "play": "Odtwórz gotowe wideo",
      "b1h": "Dodaj muzykę",
      "b1p": "Wklej link, prześlij plik lub wybierz z biblioteki.",
      "b1d": "Link · Przesyłanie · Biblioteka",
      "b2h": "Analiza i styl",
      "b2p": "Odczytujemy tempo, tonację i sekcje, a potem wybierasz styl reżyserski dla utworu.",
      "b2d": "BPM · Tonacja · Sekcje · Style",
      "b3h": "Obsada i referencje",
      "b3p": "Postać, wygląd i miejsce — Twoje zdjęcie albo wykonawca, którego zaproponujemy.",
      "b3d": "Postać · Wygląd · Miejsce",
      "b4h": "Storyboard i render",
      "b4p": "Każde ujęcie jest najpierw rozrysowane; wygeneruj ponownie, co chcesz, potem renderuj i pobierz.",
      "b4d": "Klatki · Ponów · Oś czasu · Pobierz",
      "c1": "Wklej link, prześlij plik lub wybierz z biblioteki — utwór jest pierwszy.",
      "c2": "Odczytujemy tempo, tonację, sekcje i tekst, żeby każde cięcie trafiało w muzykę.",
      "c3": "Propozycje reżysera dla Twojego utworu — każdy styl kształtuje brief, referencje i storyboard.",
      "c4": "Postać, wygląd i miejsce są zapisane jako referencje, więc w każdym ujęciu pojawia się ta sama twarz.",
      "c5": "Każde ujęcie jest rozrysowane, zanim wyrenderuje się choć jedna klatka, a wszystko, co Ci się nie podoba, można wygenerować ponownie.",
      "c6": "Finalny montaż renderuje się w kilka minut. Obejrzyj go na osi czasu, pobierz albo udostępnij link."
    },
    "faq": {
      "kicker": "Szybkie odpowiedzi",
      "h2": "Najczęstsze pytania.",
      "q1": "Ile trwa zrobienie teledysku?",
      "a1": "Darmowy pięciosekundowy podgląd renderuje się w około dwadzieścia sekund. Pełne wideo zajmuje mniej więcej trzy minuty po zatwierdzeniu storyboardu — nie czekasz w kolejce, gdy podejmujesz decyzję.",
      "q2": "Jakie audio mogę wykorzystać?",
      "a2": "Prześlij plik, wybierz coś z biblioteki albo wklej link z TikToka lub YouTube. Jeśli nie masz utworu pod ręką, wypróbuj jedną z przykładowych piosenek, żeby zobaczyć, co robi Solmi.",
      "q3": "Czy potrzebuję praw do utworu?",
      "a3": "Do wszystkiego, co publikujesz — tak: używaj muzyki własnej lub licencjonowanej. Wideo stworzone w Solmi są Twoje: zachowujesz prawa do tego, co tu tworzysz.",
      "q4": "Jakie formaty i rozdzielczości mogę eksportować?",
      "a4": "Poziomo 16:9 i pionowo 9:16, do 1080p — z opcjonalnymi napisami przy każdym eksporcie.",
      "q5": "Czy jest darmowy plan?",
      "a5": "Twoje pierwsze pięciosekundowe wideo jest darmowe i nie wymaga karty. Przed renderem poprosimy o logowanie, żeby wynik trafił do Twojej biblioteki. Pakiety kredytów i subskrypcje odblokowują pełne rendery.",
      "q6": "Czy Solmi obsługuje teksty i inne języki?",
      "a6": "Solmi odczytuje tempo, tonację, sekcje i tekst i na tej podstawie ustawia cięcia — montaż idzie z piosenką. Twórcy zrobili wideo m.in. po angielsku, hiszpańsku, francusku, polsku, rumuńsku i chińsku."
    },
    "app": {
      "kicker": "iOS i Android",
      "h2": "Zabierz Solmi ze sobą.",
      "p": "Twórz utwory, generuj teledyski, zarządzaj biblioteką i korzystaj z kredytów Solmi w aplikacji na iPhone'a, iPada i Androida.",
      "storeSmall": "Pobierz w",
      "storeBig": "App Store",
      "playSmall": "Pobierz z",
      "playBig": "Google Play",
      "qrAria": "Otwórz link QR do pobrania aplikacji Solmi",
      "qrAlt": "Kod QR strony pobierania aplikacji Solmi",
      "scan": "Zeskanuj, aby pobrać",
      "qrNote": "Kod QR otwiera stronę pobierania Solmi, która kieruje telefon do właściwego sklepu."
    },
    "close": {
      "h2a": "Twój utwór ma już swój świat.",
      "h2b": "Chodźmy go znaleźć.",
      "p": "Wybierz format, dodaj utwór i zobacz, jak renderuje się pierwsze pięć sekund."
    },
    "cta": {
      "music": "Stwórz teledysk",
      "short": "Stwórz wideo"
    },
    "dock": {
      "format": "Format",
      "notPicked": "Jeszcze nie wybrano",
      "pickFormat": "Wybierz format",
      "song": "Utwór",
      "noSong": "Brak utworu"
    },
    "footer": {
      "gallery": "Galeria",
      "pricing": "Cennik",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Regulamin",
      "privacy": "Prywatność"
    },
    "stats": {
      "aria": "Solmi w liczbach",
      "creators": "Twórców na świecie",
      "seconds": "Sekund wyrenderowanego wideo",
      "countries": "Krajów i regionów"
    },
    "header": {
      "create": "Twórz",
      "myProjects": "Moje projekty",
      "login": "Zaloguj się",
      "join": "Dołącz do Solmi za darmo",
      "joinShort": "Dołącz za darmo",
      "theme": "Zmień motyw",
      "language": "Język",
      "menu": "Menu",
      "discord": "Dołącz do naszego Discorda",
      "rewards": "Nagrody",
      "shareEarn": "Udostępniaj i zarabiaj",
      "inviteEarn": "Zapraszaj i zarabiaj",
      "affiliate": "Program partnerski",
      "account": "Konto",
      "member": "Użytkownik Solmi",
      "myProjectsMenu": "Moje projekty"
    },
    "composer": {
      "placeholder": "Prześlij utwór lub wklej link z {{platform}}",
      "describe": "Opisz utwór, który chcesz",
      "promptVideo": "Opisz swój teledysk (opcjonalnie)",
      "creating": "Tworzenie…",
      "fetching": "Pobieranie audio…",
      "uploadingPct": "Przesyłanie {{pct}}%",
      "uploadingAudio": "Przesyłanie audio…",
      "createLyric": "Stwórz wideo z tekstem",
      "makeSinging": "Stwórz wideo ze śpiewem",
      "createSong": "Stwórz utwór",
      "tooLarge": "Pliki audio mogą mieć maksymalnie {{mb}}MB.",
      "uploaded": "Audio przesłane",
      "uploadFailed": "Przesyłanie nie powiodło się",
      "couldNotSave": "Nie udało się zapisać pliku przed logowaniem. Spróbuj ponownie.",
      "invalidLink": "To nie wygląda na prawidłowy link.",
      "spotify": "Linki Spotify dają tylko 30-sekundowy podgląd, więc nie są obsługiwane. Prześlij plik audio albo wklej link z YouTube.",
      "extracting": "Wyodrębnianie audio z {{platform}}…",
      "attachedFrom": "Dodano z {{platform}}",
      "couldNotExtract": "Nie udało się wyodrębnić audio z tego linku.",
      "privatePaid": "Projekty prywatne są dostępne w planach płatnych. Ulepsz plan, aby ukryć ten projekt.",
      "noAudio": "Ten utwór nie ma jeszcze audio.",
      "processing": "Audio jest jeszcze przetwarzane — spróbuj za chwilę.",
      "couldNotStart": "Nie udało się uruchomić projektu.",
      "untitled": "Bez tytułu",
      "untitledProject": "Projekt bez tytułu",
      "reference": "Referencja",
      "attachFirst": "Wybierz utwór lub wklej link, aby zacząć",
      "describeFirst": "Najpierw opisz utwór, który chcesz",
      "selectSong": "Wybierz utwór",
      "images": "Obrazy",
      "imagesOne": "1 obraz",
      "imagesCount": "Obrazy: {{count}}",
      "imagesTitle": "Dodaj obrazy referencyjne",
      "resolutionTitle": "Rozdzielczość eksportu",
      "publicProject": "Projekt publiczny",
      "privateProject": "Projekt prywatny",
      "publicTitle": "Publiczny — widoczny w galerii Solmi. Kliknij, aby ukryć.",
      "privateTitle": "Prywatny — tylko Ty go widzisz. Kliknij, aby upublicznić.",
      "more": "Więcej",
      "aspect": "Proporcje",
      "captions": "Napisy",
      "captionsOff": "Wył.",
      "captionsHint": "Wypal stylizowany tekst w eksportowanym wideo",
      "captionsOn": "Napisy włączone ({{label}}) — kliknij, aby zmienić styl",
      "removeCaptions": "Usuń napisy",
      "uploadSong": "Prześlij utwór",
      "fileTypes": "MP3 · WAV · M4A · MP4 · do {{mb}}MB",
      "samples": "Przykładowe utwory",
      "mySongs": "Moje utwory",
      "signInSongs": "Zaloguj się, aby użyć swoich utworów Solmi.",
      "loading": "Ładowanie…",
      "noSongs": "Brak utworów.",
      "playPreview": "Odtwórz podgląd",
      "pausePreview": "Wstrzymaj podgląd",
      "removeSong": "Usuń utwór",
      "upgrade1080": "Eksport w 720p i 1080p jest dostępny w planach płatnych. Ulepsz plan, aby go odblokować."
    }
  },
  tr: {
    "hero": {
      "h1": "Her şarkıyı bir müzik videosuna dönüştür.",
      "sub": "Bir şarkı getir. Bir format seç. Müzik videonu birkaç tıkla yönet."
    },
    "picker": {
      "head": "Format seç",
      "music": "Müzik videosu",
      "musicSub": "Anlatı sahneleri",
      "singing": "Şarkı söyleme videosu",
      "singingSub": "Dudak senkronlu performans",
      "lyric": "Sözlü video",
      "lyricSub": "Sözler ekranda",
      "song": "Şarkı",
      "songSub": "Yalnızca ses",
      "mute": "Demoyu sessize al",
      "soundOn": "Demoyu sesli oynat"
    },
    "mine": {
      "projects": "Projelerim",
      "assets": "Dosyalarım"
    },
    "gallery": {
      "h2": "Solmi ile yapılan müzik videoları",
      "browse": "Tümünü gör"
    },
    "hiw": {
      "h2": "Nasıl çalışır",
      "step": "Adım {{n}}",
      "play": "Bitmiş videoyu oynat",
      "b1h": "Müzik ekle",
      "b1p": "Bir bağlantı yapıştır, dosya yükle veya kütüphanenden seç.",
      "b1d": "Bağlantı · Yükleme · Kütüphane",
      "b2h": "Analiz ve stil",
      "b2p": "Tempo, ton ve bölümleri okuruz; sonra parça için bir yönetmen stili seçersin.",
      "b2d": "BPM · Ton · Bölümler · Stiller",
      "b3h": "Kadro ve referanslar",
      "b3p": "Karakter, görünüm ve mekân — senin fotoğrafın ya da önerdiğimiz bir sanatçı.",
      "b3d": "Karakter · Görünüm · Mekân",
      "b4h": "Storyboard ve render",
      "b4p": "Her çekim önce çizilir; istediğini yeniden üret, sonra render al ve indir.",
      "b4d": "Kareler · Yeniden üret · Zaman çizelgesi · İndir",
      "c1": "Bir bağlantı yapıştır, dosya yükle veya kütüphanenden seç — önce şarkı.",
      "c2": "Tempo, ton, bölümler ve sözleri okuruz; böylece her kesim müziğe oturur.",
      "c3": "Yönetmenin parçan için seçimleri — her stil brifi, referansları ve storyboard'u yönlendirir.",
      "c4": "Karakter, görünüm ve mekân referans olarak sabitlenir; böylece her çekimde aynı yüz görünür.",
      "c5": "Tek bir kare render edilmeden önce her çekim çizilir ve beğenmediğin her şey yeniden üretilebilir.",
      "c6": "Final kurgu dakikalar içinde render edilir. Zaman çizelgesinde izle, indir ya da bağlantıyı paylaş."
    },
    "faq": {
      "kicker": "Kısa yanıtlar",
      "h2": "Sık sorulan sorular.",
      "q1": "Bir müzik videosu yapmak ne kadar sürer?",
      "a1": "Ücretsiz beş saniyelik önizleme yaklaşık yirmi saniyede render edilir. Storyboard'u onayladıktan sonra tam uzunlukta video yaklaşık üç dakika sürer — karar verirken sırada beklemezsin.",
      "q2": "Hangi sesi kullanabilirim?",
      "a2": "Bir dosya yükle, kütüphanenden seç ya da bir TikTok veya YouTube bağlantısı yapıştır. Elinde parça yoksa, Solmi'nin ne yaptığını görmek için örnek şarkılardan birini dene.",
      "q3": "Şarkının haklarına ihtiyacım var mı?",
      "a3": "Yayınladığın her şey için evet — sana ait ya da lisanslı müzik kullan. Solmi ile yaptığın videolar senindir: burada ürettiklerinin hakları sende kalır.",
      "q4": "Hangi format ve çözünürlüklerde dışa aktarabilirim?",
      "a4": "Yatay 16:9 ve dikey 9:16, 1080p'ye kadar — her dışa aktarmada isteğe bağlı altyazıyla.",
      "q5": "Ücretsiz plan var mı?",
      "a5": "İlk beş saniyelik videon ücretsiz ve kart gerektirmez. Render öncesi giriş yapman istenir; böylece sonuç kütüphanene kaydedilir. Kredi paketleri ve abonelikler tam uzunlukta render açar.",
      "q6": "Solmi şarkı sözleri ve diğer dillerle çalışır mı?",
      "a6": "Solmi tempo, ton, bölümler ve sözleri okur ve kesimleri buna göre zamanlar — kurgu şarkıyla gider. İçerik üreticileri İngilizce, İspanyolca, Fransızca, Lehçe, Rumence ve Çince gibi dillerde videolar yaptı."
    },
    "app": {
      "kicker": "iOS ve Android",
      "h2": "Solmi'yi yanında taşı.",
      "p": "iPhone, iPad ve Android uygulamasından şarkı yap, müzik videosu üret, kütüphaneni yönet ve Solmi kredilerini kullan.",
      "storeSmall": "İndir:",
      "storeBig": "App Store",
      "playSmall": "Edin:",
      "playBig": "Google Play",
      "qrAria": "Solmi uygulaması indirme QR bağlantısını aç",
      "qrAlt": "Solmi uygulaması indirme sayfası QR kodu",
      "scan": "İndirmek için tara",
      "qrNote": "QR, telefonu doğru mağazaya yönlendiren bir Solmi indirme sayfası açar."
    },
    "close": {
      "h2a": "Parçanın zaten bir dünyası var.",
      "h2b": "Hadi bulalım.",
      "p": "Bir format seç, bir şarkı getir ve ilk beş saniyenin render edilişini izle."
    },
    "cta": {
      "music": "Müzik videosu yap",
      "short": "Video yap"
    },
    "dock": {
      "format": "Format",
      "notPicked": "Henüz seçilmedi",
      "pickFormat": "Format seç",
      "song": "Şarkı",
      "noSong": "Henüz şarkı yok"
    },
    "footer": {
      "gallery": "Galeri",
      "pricing": "Fiyatlar",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Şartlar",
      "privacy": "Gizlilik"
    },
    "stats": {
      "aria": "Rakamlarla Solmi",
      "creators": "Dünya çapında üretici",
      "seconds": "Saniye render edilmiş video",
      "countries": "Ülke ve bölge"
    },
    "header": {
      "create": "Oluştur",
      "myProjects": "Projelerim",
      "login": "Giriş yap",
      "join": "Solmi'ye ücretsiz katıl",
      "joinShort": "Ücretsiz katıl",
      "theme": "Temayı değiştir",
      "language": "Dil",
      "menu": "Menü",
      "discord": "Discord'umuza katıl",
      "rewards": "Ödüller",
      "shareEarn": "Paylaş ve kazan",
      "inviteEarn": "Davet et ve kazan",
      "affiliate": "Ortaklık programı",
      "account": "Hesap",
      "member": "Solmi üyesi",
      "myProjectsMenu": "Projelerim"
    },
    "composer": {
      "placeholder": "Bir şarkı yükle veya {{platform}} bağlantısı yapıştır",
      "describe": "İstediğin şarkıyı anlat",
      "promptVideo": "Videonu anlat (isteğe bağlı)",
      "creating": "Oluşturuluyor…",
      "fetching": "Ses alınıyor…",
      "uploadingPct": "Yükleniyor %{{pct}}",
      "uploadingAudio": "Ses yükleniyor…",
      "createLyric": "Sözlü video yap",
      "makeSinging": "Şarkı söyleme videosu yap",
      "createSong": "Şarkı oluştur",
      "tooLarge": "Ses dosyaları en fazla {{mb}}MB olmalı.",
      "uploaded": "Ses yüklendi",
      "uploadFailed": "Yükleme başarısız",
      "couldNotSave": "Giriş öncesi bu dosya kaydedilemedi. Lütfen tekrar dene.",
      "invalidLink": "Bu geçerli bir bağlantıya benzemiyor.",
      "spotify": "Spotify bağlantıları yalnızca 30 saniyelik önizleme sunar, bu yüzden desteklenmez. Ses dosyasını yükle ya da bir YouTube bağlantısı yapıştır.",
      "extracting": "{{platform}} kaynağından ses çıkarılıyor…",
      "attachedFrom": "{{platform}} kaynağından eklendi",
      "couldNotExtract": "Bu bağlantıdan ses çıkarılamadı.",
      "privatePaid": "Özel projeler ücretli planlarda mevcut. Bu projeyi özel yapmak için yükselt.",
      "noAudio": "Bu parçanın henüz sesi yok.",
      "processing": "Ses hâlâ işleniyor — biraz sonra tekrar dene.",
      "couldNotStart": "Proje başlatılamadı.",
      "untitled": "Adsız",
      "untitledProject": "Adsız proje",
      "reference": "Referans",
      "attachFirst": "Başlamak için bir şarkı seç veya bağlantı yapıştır",
      "describeFirst": "Önce istediğin şarkıyı anlat",
      "selectSong": "Şarkı seç",
      "images": "Görseller",
      "imagesOne": "1 görsel",
      "imagesCount": "{{count}} görsel",
      "imagesTitle": "Referans görseli ekle",
      "resolutionTitle": "Dışa aktarma çözünürlüğü",
      "publicProject": "Herkese açık proje",
      "privateProject": "Özel proje",
      "publicTitle": "Herkese açık — Solmi galerisinde görünür. Özel yapmak için tıkla.",
      "privateTitle": "Özel — yalnızca sen görebilirsin. Herkese açmak için tıkla.",
      "more": "Daha fazla",
      "aspect": "En-boy oranı",
      "captions": "Altyazı",
      "captionsOff": "Kapalı",
      "captionsHint": "Stilize sözleri dışa aktarılan videoya işle",
      "captionsOn": "Altyazı açık ({{label}}) — stili değiştirmek için tıkla",
      "removeCaptions": "Altyazıyı kaldır",
      "uploadSong": "Şarkı yükle",
      "fileTypes": "MP3 · WAV · M4A · MP4 · en fazla {{mb}}MB",
      "samples": "Örnek şarkılar",
      "mySongs": "Şarkılarım",
      "signInSongs": "Solmi şarkılarını kullanmak için giriş yap.",
      "loading": "Yükleniyor…",
      "noSongs": "Henüz şarkı yok.",
      "playPreview": "Önizlemeyi oynat",
      "pausePreview": "Önizlemeyi duraklat",
      "removeSong": "Şarkıyı kaldır",
      "upgrade1080": "720p ve 1080p dışa aktarma ücretli planlarda mevcut. Açmak için yükselt."
    }
  },
  vi: {
    "hero": {
      "h1": "Biến bất kỳ bài hát nào thành video ca nhạc.",
      "sub": "Mang một bài hát. Chọn định dạng. Đạo diễn video ca nhạc chỉ với vài cú nhấp."
    },
    "picker": {
      "head": "Chọn định dạng",
      "music": "Video ca nhạc",
      "musicSub": "Cảnh kể chuyện",
      "singing": "Video hát",
      "singingSub": "Trình diễn nhép môi",
      "lyric": "Video lời bài hát",
      "lyricSub": "Lời trên màn hình",
      "song": "Bài hát",
      "songSub": "Chỉ âm thanh",
      "mute": "Tắt tiếng demo",
      "soundOn": "Phát demo có tiếng"
    },
    "mine": {
      "projects": "Dự án của tôi",
      "assets": "Tệp của tôi"
    },
    "gallery": {
      "h2": "Video ca nhạc được tạo bằng Solmi",
      "browse": "Xem tất cả"
    },
    "hiw": {
      "h2": "Cách hoạt động",
      "step": "Bước {{n}}",
      "play": "Phát video hoàn chỉnh",
      "b1h": "Thêm nhạc",
      "b1p": "Dán liên kết, tải tệp lên hoặc chọn từ thư viện.",
      "b1d": "Liên kết · Tải lên · Thư viện",
      "b2h": "Phân tích & phong cách",
      "b2p": "Chúng tôi đọc tempo, tông và các phần, rồi bạn chọn phong cách đạo diễn cho bài hát.",
      "b2d": "BPM · Tông · Các phần · Phong cách",
      "b3h": "Dàn diễn viên & tham chiếu",
      "b3p": "Nhân vật, diện mạo và bối cảnh — ảnh của bạn hoặc một nghệ sĩ chúng tôi gợi ý.",
      "b3d": "Nhân vật · Diện mạo · Bối cảnh",
      "b4h": "Storyboard & kết xuất",
      "b4p": "Mỗi cảnh được phác thảo trước; tạo lại tùy ý, rồi kết xuất và tải về.",
      "b4d": "Khung hình · Tạo lại · Dòng thời gian · Tải về",
      "c1": "Dán liên kết, tải tệp lên hoặc chọn từ thư viện — bài hát đi trước.",
      "c2": "Chúng tôi đọc tempo, tông, các phần và lời để mỗi cú cắt rơi đúng nhịp nhạc.",
      "c3": "Lựa chọn của đạo diễn cho bài hát của bạn — mỗi phong cách dẫn dắt brief, tham chiếu và storyboard.",
      "c4": "Nhân vật, diện mạo và bối cảnh được khóa làm tham chiếu, nên cùng một gương mặt xuất hiện trong mọi cảnh.",
      "c5": "Mỗi cảnh được phác thảo trước khi kết xuất một khung hình nào, và bạn có thể tạo lại bất cứ thứ gì không ưng.",
      "c6": "Bản dựng cuối kết xuất trong vài phút. Xem trên dòng thời gian, tải về hoặc chia sẻ liên kết."
    },
    "faq": {
      "kicker": "Trả lời nhanh",
      "h2": "Câu hỏi thường gặp.",
      "q1": "Làm một video ca nhạc mất bao lâu?",
      "a1": "Bản xem trước 5 giây miễn phí kết xuất trong khoảng 20 giây. Video đầy đủ mất khoảng 3 phút sau khi bạn duyệt storyboard — không phải xếp hàng chờ trong lúc bạn quyết định.",
      "q2": "Tôi có thể dùng âm thanh nào?",
      "a2": "Tải tệp lên, chọn từ thư viện hoặc dán liên kết TikTok hay YouTube. Nếu chưa có bài hát, hãy thử một bài mẫu để xem Solmi làm được gì.",
      "q3": "Tôi có cần bản quyền bài hát không?",
      "a3": "Với mọi thứ bạn công bố thì có — hãy dùng nhạc bạn sở hữu hoặc được cấp phép. Video bạn tạo bằng Solmi là của bạn: bạn giữ quyền với những gì bạn tạo ở đây.",
      "q4": "Tôi có thể xuất định dạng và độ phân giải nào?",
      "a4": "Ngang 16:9 và dọc 9:16, tối đa 1080p — có thể thêm phụ đề ở mọi lần xuất.",
      "q5": "Có gói miễn phí không?",
      "a5": "Video 5 giây đầu tiên miễn phí và không cần thẻ. Bạn sẽ được yêu cầu đăng nhập trước khi kết xuất để kết quả lưu vào thư viện. Gói tín dụng và đăng ký mở khóa kết xuất đầy đủ.",
      "q6": "Solmi có xử lý lời bài hát và ngôn ngữ khác không?",
      "a6": "Solmi đọc tempo, tông, các phần và lời để căn thời điểm cắt — bản dựng đi theo bài hát. Người sáng tạo đã làm video bằng tiếng Anh, Tây Ban Nha, Pháp, Ba Lan, Romania và Trung Quốc, cùng nhiều ngôn ngữ khác."
    },
    "app": {
      "kicker": "iOS và Android",
      "h2": "Mang Solmi theo bạn.",
      "p": "Tạo bài hát, sinh video ca nhạc, quản lý thư viện và dùng tín dụng Solmi từ ứng dụng iPhone, iPad và Android.",
      "storeSmall": "Tải trên",
      "storeBig": "App Store",
      "playSmall": "Tải trên",
      "playBig": "Google Play",
      "qrAria": "Mở liên kết QR tải ứng dụng Solmi",
      "qrAlt": "Mã QR trang tải ứng dụng Solmi",
      "scan": "Quét để tải",
      "qrNote": "Mã QR mở trang tải Solmi và đưa điện thoại đến đúng cửa hàng."
    },
    "close": {
      "h2a": "Bài hát của bạn đã có một thế giới.",
      "h2b": "Cùng đi tìm nó.",
      "p": "Chọn định dạng, mang một bài hát và xem 5 giây đầu tiên được kết xuất."
    },
    "cta": {
      "music": "Tạo video ca nhạc",
      "short": "Tạo video"
    },
    "dock": {
      "format": "Định dạng",
      "notPicked": "Chưa chọn",
      "pickFormat": "Chọn định dạng",
      "song": "Bài hát",
      "noSong": "Chưa có bài hát"
    },
    "footer": {
      "gallery": "Thư viện",
      "pricing": "Bảng giá",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "Điều khoản",
      "privacy": "Quyền riêng tư"
    },
    "stats": {
      "aria": "Solmi qua những con số",
      "creators": "Người sáng tạo toàn cầu",
      "seconds": "Giây video đã kết xuất",
      "countries": "Quốc gia & vùng lãnh thổ"
    },
    "header": {
      "create": "Tạo",
      "myProjects": "Dự án của tôi",
      "login": "Đăng nhập",
      "join": "Tham gia Solmi miễn phí",
      "joinShort": "Tham gia miễn phí",
      "theme": "Đổi giao diện",
      "language": "Ngôn ngữ",
      "menu": "Menu",
      "discord": "Tham gia Discord của chúng tôi",
      "rewards": "Phần thưởng",
      "shareEarn": "Chia sẻ & nhận thưởng",
      "inviteEarn": "Mời & nhận thưởng",
      "affiliate": "Chương trình tiếp thị liên kết",
      "account": "Tài khoản",
      "member": "Thành viên Solmi",
      "myProjectsMenu": "Dự án của tôi"
    },
    "composer": {
      "placeholder": "Tải bài hát lên hoặc dán liên kết {{platform}}",
      "describe": "Mô tả bài hát bạn muốn",
      "promptVideo": "Mô tả video của bạn (tùy chọn)",
      "creating": "Đang tạo…",
      "fetching": "Đang lấy âm thanh…",
      "uploadingPct": "Đang tải lên {{pct}}%",
      "uploadingAudio": "Đang tải âm thanh lên…",
      "createLyric": "Tạo video lời bài hát",
      "makeSinging": "Tạo video hát",
      "createSong": "Tạo bài hát",
      "tooLarge": "Tệp âm thanh tối đa {{mb}}MB.",
      "uploaded": "Đã tải âm thanh lên",
      "uploadFailed": "Tải lên thất bại",
      "couldNotSave": "Không thể lưu tệp này trước khi đăng nhập. Vui lòng thử lại.",
      "invalidLink": "Đây có vẻ không phải liên kết hợp lệ.",
      "spotify": "Liên kết Spotify chỉ cho nghe thử 30 giây nên không được hỗ trợ. Hãy tải tệp âm thanh lên hoặc dán liên kết YouTube.",
      "extracting": "Đang trích xuất âm thanh từ {{platform}}…",
      "attachedFrom": "Đã thêm từ {{platform}}",
      "couldNotExtract": "Không thể trích xuất âm thanh từ liên kết đó.",
      "privatePaid": "Dự án riêng tư có trong các gói trả phí. Nâng cấp để đặt dự án này ở chế độ riêng tư.",
      "noAudio": "Bài này chưa có âm thanh.",
      "processing": "Âm thanh vẫn đang xử lý — thử lại sau giây lát.",
      "couldNotStart": "Không thể bắt đầu dự án.",
      "untitled": "Chưa đặt tên",
      "untitledProject": "Dự án chưa đặt tên",
      "reference": "Tham chiếu",
      "attachFirst": "Chọn bài hát hoặc dán liên kết để bắt đầu",
      "describeFirst": "Hãy mô tả bài hát bạn muốn trước",
      "selectSong": "Chọn bài hát",
      "images": "Hình ảnh",
      "imagesOne": "1 ảnh",
      "imagesCount": "{{count}} ảnh",
      "imagesTitle": "Thêm ảnh tham chiếu",
      "resolutionTitle": "Độ phân giải xuất",
      "publicProject": "Dự án công khai",
      "privateProject": "Dự án riêng tư",
      "publicTitle": "Công khai — hiển thị trong thư viện Solmi. Nhấp để chuyển riêng tư.",
      "privateTitle": "Riêng tư — chỉ bạn xem được. Nhấp để chuyển công khai.",
      "more": "Thêm",
      "aspect": "Tỷ lệ khung hình",
      "captions": "Phụ đề",
      "captionsOff": "Tắt",
      "captionsHint": "Ghi lời bài hát có phong cách vào video xuất",
      "captionsOn": "Phụ đề bật ({{label}}) — nhấp để đổi phong cách",
      "removeCaptions": "Xóa phụ đề",
      "uploadSong": "Tải bài hát lên",
      "fileTypes": "MP3 · WAV · M4A · MP4 · tối đa {{mb}}MB",
      "samples": "Bài hát mẫu",
      "mySongs": "Bài hát của tôi",
      "signInSongs": "Đăng nhập để dùng bài hát Solmi của bạn.",
      "loading": "Đang tải…",
      "noSongs": "Chưa có bài hát.",
      "playPreview": "Phát thử",
      "pausePreview": "Tạm dừng",
      "removeSong": "Bỏ bài hát",
      "upgrade1080": "Xuất 720p và 1080p có trong các gói trả phí. Nâng cấp để mở khóa."
    }
  },
  de: {
    "hero": {
      "h1": "Mach aus jedem Song ein Musikvideo.",
      "sub": "Bring einen Song mit. Wähl ein Format. Führe Regie mit wenigen Klicks."
    },
    "picker": {
      "head": "Format wählen",
      "music": "Musikvideo",
      "musicSub": "Erzählte Szenen",
      "singing": "Gesangsvideo",
      "singingSub": "Lipsync-Performance",
      "lyric": "Lyric-Video",
      "lyricSub": "Text im Bild",
      "song": "Song",
      "songSub": "Nur Audio",
      "mute": "Demo stummschalten",
      "soundOn": "Demo mit Ton abspielen"
    },
    "mine": {
      "projects": "Meine Projekte",
      "assets": "Meine Dateien"
    },
    "gallery": {
      "h2": "Mit Solmi erstellte Musikvideos",
      "browse": "Alle ansehen"
    },
    "hiw": {
      "h2": "So funktioniert es",
      "step": "Schritt {{n}}",
      "play": "Fertiges Video abspielen",
      "b1h": "Musik hinzufügen",
      "b1p": "Link einfügen, Datei hochladen oder aus der Bibliothek wählen.",
      "b1d": "Link · Upload · Bibliothek",
      "b2h": "Analyse & Stil",
      "b2p": "Wir lesen Tempo, Tonart und Abschnitte, dann wählst du einen Regie-Stil für den Track.",
      "b2d": "BPM · Tonart · Abschnitte · Stile",
      "b3h": "Besetzung & Referenzen",
      "b3p": "Figur, Look und Ort – dein Foto oder ein vorgeschlagener Performer.",
      "b3d": "Figur · Look · Ort",
      "b4h": "Storyboard & Render",
      "b4p": "Jede Einstellung wird zuerst gezeichnet; erneuere, was du willst, dann rendern und herunterladen.",
      "b4d": "Frames · Neu generieren · Timeline · Download",
      "c1": "Link einfügen, Datei hochladen oder aus der Bibliothek wählen – der Song kommt zuerst.",
      "c2": "Wir lesen Tempo, Tonart, Abschnitte und Text, damit jeder Schnitt auf der Musik sitzt.",
      "c3": "Die Vorschläge der Regie für deinen Track – jeder Stil prägt Briefing, Referenzen und Storyboard.",
      "c4": "Figur, Look und Ort werden als Referenzen festgelegt, damit in jeder Einstellung dasselbe Gesicht erscheint.",
      "c5": "Jede Einstellung wird gezeichnet, bevor ein einziges Bild gerendert wird – und alles, was dir nicht gefällt, lässt sich neu generieren.",
      "c6": "Der finale Schnitt rendert in Minuten. Sieh ihn auf der Timeline an, lade ihn herunter oder teile den Link."
    },
    "faq": {
      "kicker": "Kurze Antworten",
      "h2": "Häufige Fragen.",
      "q1": "Wie lange dauert ein Musikvideo?",
      "a1": "Die kostenlose Fünf-Sekunden-Vorschau rendert in etwa zwanzig Sekunden. Ein komplettes Video dauert rund drei Minuten, sobald du das Storyboard freigegeben hast – du wartest nicht in einer Warteschlange, während du entscheidest.",
      "q2": "Welches Audio kann ich mitbringen?",
      "a2": "Lade eine Datei hoch, wähle etwas aus deiner Bibliothek oder füge einen TikTok- oder YouTube-Link ein. Ohne eigenen Track probier einen der Beispielsongs, um zu sehen, was Solmi kann.",
      "q3": "Brauche ich die Rechte am Song?",
      "a3": "Für alles, was du veröffentlichst, ja – nutze Musik, die dir gehört oder lizenziert ist. Videos, die du mit Solmi machst, gehören dir: Die Rechte an dem, was du hier erstellst, bleiben bei dir.",
      "q4": "Welche Formate und Auflösungen kann ich exportieren?",
      "a4": "Querformat 16:9 und Hochformat 9:16, bis 1080p – mit optionalen Untertiteln bei jedem Export.",
      "q5": "Gibt es einen kostenlosen Tarif?",
      "a5": "Dein erstes Fünf-Sekunden-Video ist kostenlos, ohne Karte. Vor dem Rendern wirst du zum Anmelden gebeten, damit das Ergebnis in deiner Bibliothek landet. Credit-Pakete und Abos schalten Renders in voller Länge frei.",
      "q6": "Versteht Solmi Songtexte und andere Sprachen?",
      "a6": "Solmi liest Tempo, Tonart, Abschnitte und Text und setzt die Schnitte danach – der Schnitt sitzt auf dem Song. Creator haben Videos u. a. auf Englisch, Spanisch, Französisch, Polnisch, Rumänisch und Chinesisch gemacht."
    },
    "app": {
      "kicker": "iOS und Android",
      "h2": "Nimm Solmi mit.",
      "p": "Erstelle Songs, generiere Musikvideos, verwalte deine Bibliothek und nutze deine Solmi-Credits in der App für iPhone, iPad und Android.",
      "storeSmall": "Laden im",
      "storeBig": "App Store",
      "playSmall": "Jetzt bei",
      "playBig": "Google Play",
      "qrAria": "QR-Link zum Download der Solmi-App öffnen",
      "qrAlt": "QR-Code zur Download-Seite der Solmi-App",
      "scan": "Scannen zum Laden",
      "qrNote": "Der QR öffnet eine Solmi-Download-Seite, die jedes Handy zum richtigen Store führt."
    },
    "close": {
      "h2a": "Dein Track hat schon eine Welt.",
      "h2b": "Lass sie uns finden.",
      "p": "Wähl ein Format, bring einen Song mit und sieh zu, wie die ersten fünf Sekunden rendern."
    },
    "cta": {
      "music": "Musikvideo erstellen",
      "short": "Video erstellen"
    },
    "dock": {
      "format": "Format",
      "notPicked": "Noch nicht gewählt",
      "pickFormat": "Format wählen",
      "song": "Song",
      "noSong": "Noch kein Song"
    },
    "footer": {
      "gallery": "Galerie",
      "pricing": "Preise",
      "blog": "Blog",
      "discord": "Discord",
      "terms": "AGB",
      "privacy": "Datenschutz"
    },
    "stats": {
      "aria": "Solmi in Zahlen",
      "creators": "Creator weltweit",
      "seconds": "Sekunden gerendertes Video",
      "countries": "Länder und Regionen"
    },
    "header": {
      "create": "Erstellen",
      "myProjects": "Meine Projekte",
      "login": "Anmelden",
      "join": "Kostenlos bei Solmi mitmachen",
      "joinShort": "Kostenlos starten",
      "theme": "Design wechseln",
      "language": "Sprache",
      "menu": "Menü",
      "discord": "Tritt unserem Discord bei",
      "rewards": "Prämien",
      "shareEarn": "Teilen & verdienen",
      "inviteEarn": "Einladen & verdienen",
      "affiliate": "Partnerprogramm",
      "account": "Konto",
      "member": "Solmi-Mitglied",
      "myProjectsMenu": "Meine Projekte"
    },
    "composer": {
      "placeholder": "Song hochladen oder {{platform}}-Link einfügen",
      "describe": "Beschreib den Song, den du willst",
      "promptVideo": "Beschreibe dein Video (optional)",
      "creating": "Wird erstellt…",
      "fetching": "Audio wird geholt…",
      "uploadingPct": "Upload {{pct}} %",
      "uploadingAudio": "Audio wird hochgeladen…",
      "createLyric": "Lyric-Video erstellen",
      "makeSinging": "Gesangsvideo erstellen",
      "createSong": "Song erstellen",
      "tooLarge": "Audiodateien dürfen höchstens {{mb}} MB groß sein.",
      "uploaded": "Audio hochgeladen",
      "uploadFailed": "Upload fehlgeschlagen",
      "couldNotSave": "Die Datei konnte vor der Anmeldung nicht gespeichert werden. Bitte erneut versuchen.",
      "invalidLink": "Das sieht nicht nach einem gültigen Link aus.",
      "spotify": "Spotify-Links liefern nur eine 30-Sekunden-Vorschau und werden nicht unterstützt. Lade die Audiodatei hoch oder füge einen YouTube-Link ein.",
      "extracting": "Audio wird von {{platform}} extrahiert…",
      "attachedFrom": "Von {{platform}} übernommen",
      "couldNotExtract": "Aus diesem Link ließ sich kein Audio extrahieren.",
      "privatePaid": "Private Projekte gibt es in den Bezahltarifen. Upgrade, um dieses Projekt privat zu machen.",
      "noAudio": "Dieser Track hat noch kein Audio.",
      "processing": "Das Audio wird noch verarbeitet – versuch es gleich noch einmal.",
      "couldNotStart": "Das Projekt konnte nicht gestartet werden.",
      "untitled": "Ohne Titel",
      "untitledProject": "Projekt ohne Titel",
      "reference": "Referenz",
      "attachFirst": "Wähl einen Song oder füg einen Link ein, um zu starten",
      "describeFirst": "Beschreib zuerst den Song, den du willst",
      "selectSong": "Song wählen",
      "images": "Bilder",
      "imagesOne": "1 Bild",
      "imagesCount": "{{count}} Bilder",
      "imagesTitle": "Referenzbilder hinzufügen",
      "resolutionTitle": "Exportauflösung",
      "publicProject": "Öffentliches Projekt",
      "privateProject": "Privates Projekt",
      "publicTitle": "Öffentlich – erscheint in der Solmi-Galerie. Klicken, um es privat zu machen.",
      "privateTitle": "Privat – nur du siehst es. Klicken, um es öffentlich zu machen.",
      "more": "Mehr",
      "aspect": "Seitenverhältnis",
      "captions": "Untertitel",
      "captionsOff": "Aus",
      "captionsHint": "Gestalteten Songtext ins exportierte Video einbrennen",
      "captionsOn": "Untertitel an ({{label}}) – klicken, um den Stil zu ändern",
      "removeCaptions": "Untertitel entfernen",
      "uploadSong": "Song hochladen",
      "fileTypes": "MP3 · WAV · M4A · MP4 · bis {{mb}} MB",
      "samples": "Beispielsongs",
      "mySongs": "Meine Songs",
      "signInSongs": "Melde dich an, um deine Solmi-Songs zu nutzen.",
      "loading": "Lädt…",
      "noSongs": "Noch keine Songs.",
      "playPreview": "Vorschau abspielen",
      "pausePreview": "Vorschau pausieren",
      "removeSong": "Song entfernen",
      "upgrade1080": "Exporte in 720p und 1080p gibt es in den Bezahltarifen. Upgrade, um sie freizuschalten."
    }
  },
};

export default landingV2;
